import { collectAliasUsages, flowDefaultAction, flowEventRules, attemptsToFloor, branchDepth, DEFAULT_FLOW_CONFIG, deriveGraph, fitsBranchDepth, internalDepth, MAX_POINTS, normalizeTerminals, resolveSelectionParents, scoreLadder, type FlowAction, type SensorRung, type TaskFlowConfig, type AnswerRefAlias } from '@agavi/blocks';
import { Ban, CheckSquare, ChevronsDownUp, ChevronsUpDown, ChevronUp, Copy, CopyPlus, Eraser, Flag, LogOut, Map as MapIcon, Play, Trash2, TrendingDown, TrendingUp, TriangleAlert, Trophy, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { ClipboardTask, ClipboardTasks } from '@/lib/clipboard';
import type { PullRow } from '@/lib/pull-through';
import type { Experience, ScoringDefault, ScoringMode, Task, TaskVariable } from '@/types/experience';

/** Set one event's flow action, keyed the way deriveTaskEvents ids them: 'correct' →
 * allCorrectAction, 'incorrect' → defaultAction, 'noAnswer' → noAnswerAction, else a
 * special rule's id → that rule's action. Returns a fresh config (never mutates). */
function withEventAction(flow: TaskFlowConfig, eventId: string, action: FlowAction): TaskFlowConfig {
  if (eventId === 'correct') return { ...flow, allCorrectAction: action };
  if (eventId === 'incorrect') return { ...flow, defaultAction: action };
  if (eventId === 'noAnswer') return { ...flow, noAnswerAction: action };
  return { ...flow, eventRules: flowEventRules(flow).map((r) => (r.id === eventId ? { ...r, action } : r)) };
}

/** Every task id a flow's goto actions point at. */
function flowGotoTargets(flow?: TaskFlowConfig): string[] {
  if (!flow) return [];
  // The sensor rungs were missing here, and had been since they were added: a task reachable ONLY
  // from a sensor rung did not count as pointed at, so cutting a boundary wire left that rung
  // aiming at a task on the other side of it.
  const rungActs = (r?: SensorRung): FlowAction[] => (r ? [r.action, ...(r.rules ?? []).map((x) => x.action)] : []);
  const acts: FlowAction[] = [
    flow.noAnswerAction, flowDefaultAction(flow), flow.allCorrectAction,
    ...flowEventRules(flow).map((r) => r.action),
    ...rungActs(flow.noSensorData), ...rungActs(flow.poorSensorData),
  ];
  return acts.flatMap((a) => (a.type === 'goto' && a.targetTaskId ? [a.targetTaskId] : []));
}

/** Revert every goto action whose target is in `ids` back to retry-in-place (cut a boundary wire). */
function cutFlowTargeting(flow: TaskFlowConfig, ids: Set<string>): TaskFlowConfig {
  const fix = (a: FlowAction): FlowAction => (a.type === 'goto' && a.targetTaskId && ids.has(a.targetTaskId) ? { type: 'stay' } : a);
  // Both halves of a rung: the consolidated action AND the split rows, because either can be the
  // one currently in force and the other has to survive the cut intact.
  const fixRung = (r?: SensorRung): SensorRung => ({
    action: fix(r?.action ?? { type: 'stay' }),
    split: r?.split === true,
    rules: (r?.rules ?? []).map((x) => ({ ...x, action: fix(x.action) })),
  });
  // Spread, don't rebuild: `changes` holds no destination, but it (and anything else the config
  // grows) has to survive a wire cut untouched.
  return {
    ...flow,
    noAnswerAction: fix(flow.noAnswerAction),
    defaultAction: fix(flowDefaultAction(flow)),
    allCorrectAction: fix(flow.allCorrectAction),
    eventRules: flowEventRules(flow).map((r) => ({ ...r, action: fix(r.action) })),
    noSensorData: fixRung(flow.noSensorData),
    poorSensorData: fixRung(flow.poorSensorData),
  };
}

import BuilderMapModal from '@/components/experience/builder/builder-map-modal';
import SpineBuilder from '@/components/experience/builder/spine-builder';
import DeleteConfirmModal from '@/components/experience/delete-confirm-modal';
import ExperienceNavbar from '@/components/experience/experience-navbar';
import ExperienceTitleBar from '@/components/experience/experience-title-bar';
import MiniMapModal from '@/components/experience/mini-map-modal';
import ProblemsPanel from '@/components/experience/problems-panel';
import UnsavedChangesModal from '@/components/experience/unsaved-changes-modal';
import WorkspaceCanvas from '@/components/experience/workspace-canvas';
import WorkspaceContent from '@/components/experience/workspace-content';
import { usePreviewSession } from '@/components/preview-session-context';
import TakeOverBanner from '@/components/take-over-banner';
import PullThroughDialog from '@/components/task/pull-through-dialog';
import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useCapabilities } from '@/hooks/use-capabilities';
import { useTaskTakeOver } from '@/hooks/use-task-take-over';
import { useWorkspaceCanvas } from '@/hooks/use-workspace-canvas';
import { api } from '@/lib/api';
import { foldExperienceVariables, isSameDocumentPaste, readClipboard, snapshotTask, subscribeClipboard, withPulledAliases, writeClipboard } from '@/lib/clipboard';
import { experienceProblems, groupProblems, summariseByTask, type ExperienceProblem } from '@/lib/experience-problems';
import { migrateConditionShape } from '@/lib/flow-utils';
import { classifyLoadFailure } from '@/lib/load-failure';
import { describeError, recordLoadEvent, sampleIds } from '@/lib/load-recorder';
import { alreadyHereIds, pullPasteOrder, pullRows } from '@/lib/pull-through';
import { commitScore, formatScore, sanitizeScoreDraft } from '@/lib/score-input';
import { duplicateAnchorId, orphanedHosts, selectionShortcut } from '@/lib/selection';
import { diffBoard, tasksToPointAt } from '@/lib/task-diff';
import { recordUndoEvent } from '@/lib/undo-recorder';


// ── Undo/redo history ────────────────────────────────────────────────────────

interface HistoryEntry {
  tasks: Task[];
  // The experience-level scoring default rides in the undo stack so changing it reverts with one Undo,
  // same as Apply to Main Flow / Clear Pathway Scores (which change task points). Persisted separately.
  scoringDefault?: ScoringDefault;
}

const LADDER_SHOWN = 6; // longest score ladder written out in full before it states a count instead
const MAX_HISTORY = 100; // deepest undo we keep; bounds the stack so a long editing session can't grow it without limit

/** Scoring seeded onto a NEW task at creation: a task created inside a pathway is always ungraded
 * (points are earned on the mastery path); a spine task inherits the experience default. Absent
 * default ⇒ ungraded. Returns the three scoring fields to spread into the new task. */
function newTaskScoring(def: ScoringDefault | undefined, isPathway: boolean): Pick<Task, 'points' | 'penalty' | 'scoringMode'> {
  if (isPathway || !def || def.mode === 'ungraded') return { scoringMode: 'ungraded', points: 0, penalty: 0 };
  if (def.mode === 'milestone') return { scoringMode: 'milestone', points: def.points, penalty: 0 };
  return { scoringMode: 'penalty', points: def.points, penalty: def.stepDown };
}

/** A compact numeric input for the scoring default strip. Digits-only, commits on blur/Enter (so it
 * doesn't spam undo/autosave per keystroke), clamps to `min` (1 for milestone/penalty points — 0 is
 * expressed by the Ungraded mode). A penalty (`negative`) shows a leading − (magnitude stored positive). */
function ScoringStepper({ label, value, onChange, disabled, min = 0, max = MAX_POINTS, negative }: { label: string; value: number; onChange: (v: number) => void; disabled?: boolean; min?: number; max?: number; negative?: boolean }) {
  const [draft, setDraft] = useState(formatScore(value));
  useEffect(() => setDraft(formatScore(value)), [value]);
  const commit = () => {
    const v = commitScore(draft, value, { min, max });
    setDraft(formatScore(v));
    if (v !== value) onChange(v);
  };
  return (
    <label className='flex items-center gap-1.5 text-xs font-medium text-ws-muted'>
      {label}
      <span className={`flex items-center rounded-md border border-ws-border bg-ws-surface text-sm text-ws-text focus-within:border-ws-primary ${disabled ? 'opacity-40' : ''}`}>
        {negative && <span aria-hidden='true' className='pl-1.5 text-ws-muted'>−</span>}
        <input
          type='text' inputMode='decimal' value={draft} disabled={disabled}
          onChange={(e) => setDraft(sanitizeScoreDraft(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          aria-label={label}
          className={`w-9 border-0 bg-transparent py-1 text-center tabular-nums text-ws-text focus:outline-none ${negative ? 'pl-0.5 pr-1.5' : 'px-1.5'}`}
        />
      </span>
    </label>
  );
}

/**
/**
 * Per-lane point breakdown (static, O(tasks)): the Main Flow baseline (every un-pathway'd task) + each
 * pathway's own total, in board order. Two authoring-time imbalance signals per pathway:
 *   • total > 0 (`anyGraded`)     — the pathway ADDS points on top of the main flow (can earn MORE).
 *   • endsMissing > 0             — the pathway ENDS without returning to the spine, so its students
 *                                   skip `endsMissing` pts of graded main-flow tail (can earn LESS).
 * The second is derived from the flow graph: a branch only returns via an edge back to a spine task
 * (deriveGraph); otherwise it dead-ends, and everything on the spine after its branch point is missed.
 * Both are v1 authoring aids — the deferred "% of attainable on the path taken" runtime model supersedes
 * them. Staged (Unused) tasks sit outside the flow, so they're excluded. Shared by the panel and the peek.
 */
function scoringBreakdown(tasks: Task[]) {
  // Was `tasks.filter(t => !t.staged)`. Nothing ever set `staged`, and the dock it belonged to was
  // abandoned, so every task is an active one.
  const active = tasks;
  const g = deriveGraph(active);
  const spine = g.spine;
  const spineIndex = new Map<string, number>(spine.map((id, i) => [id, i]));
  const spineSet = new Set(spine);
  const byId = new Map(active.map((t) => [t.id, t]));

  // Graded points on the spine strictly AFTER index i (the tail a premature end would skip).
  const tailAfter = (i: number) => {
    let s = 0;
    for (let k = i + 1; k < spine.length; k++) s += byId.get(spine[k]!)?.points ?? 0;
    return s;
  };
  // Climb pathwayOf from a pathway's host up to its spine ancestor, returning that ancestor's spine index.
  const hostSpineIndex = (headHost: string | null | undefined) => {
    let curId: string | null | undefined = headHost;
    let guard = 0;
    while (curId && guard++ < 64) {
      if (spineIndex.has(curId)) return spineIndex.get(curId);
      curId = byId.get(curId)?.pathwayOf;
    }
    return undefined;
  };

  // Which tasks something routes INTO (any edge target). A pathway whose head has no incoming edge has
  // "no entrance" — the builder already flags that separately (same test), so we skip BOTH scoring
  // warnings on it: an unreachable pathway can neither add points nor make anyone miss the main-flow tail.
  const incoming = new Set<string>();
  for (const e of g.edges) if (e.toId) incoming.add(e.toId);

  let mainFlowTotal = 0;
  const pathways: { id: string; name: string; total: number; endsMissing: number; reachable: boolean }[] = [];
  const pwIndex = new Map<string, number>();
  const pwHost = new Map<string, string | null | undefined>(); // pathwayId -> head's pathwayOf (branch point)
  const pwHead = new Map<string, string>();                     // pathwayId -> head task id (for reachability)
  const pwMembers = new Map<string, Set<string>>();
  for (const tk of active) {
    const p = tk.points ?? 0;
    if (tk.pathwayId) {
      let idx = pwIndex.get(tk.pathwayId);
      if (idx === undefined) {
        idx = pathways.length;
        pwIndex.set(tk.pathwayId, idx);
        pathways.push({ id: tk.pathwayId, name: (tk.pathwayName ?? '').trim(), total: 0, endsMissing: 0, reachable: false });
        pwHost.set(tk.pathwayId, tk.pathwayOf); // first-seen member in array order = the head
        pwHead.set(tk.pathwayId, tk.id);
        pwMembers.set(tk.pathwayId, new Set());
      }
      pathways[idx]!.total += p;
      pwMembers.get(tk.pathwayId)!.add(tk.id);
    } else {
      mainFlowTotal += p; // spine tasks (and any un-pathway'd task) roll into the Main Flow baseline
    }
  }

  for (const pw of pathways) {
    pw.reachable = incoming.has(pwHead.get(pw.id)!);
    if (!pw.reachable) continue; // no entrance → unreachable; a separate warning owns it, moot for scoring
    const members = pwMembers.get(pw.id)!;
    const returnsToSpine = g.edges.some((e) => members.has(e.fromId) && e.toId != null && spineSet.has(e.toId));
    if (returnsToSpine) continue; // reaches the spine again → no tail skipped
    const hi = hostSpineIndex(pwHost.get(pw.id));
    if (hi === undefined) continue;
    pw.endsMissing = tailAfter(hi); // 0 if host is the last spine task or the tail is ungraded
  }

  return {
    mainFlowTotal,
    pathways,
    hasPathways: pathways.length > 0,
    anyGraded: pathways.some((p) => p.reachable && p.total > 0),
    anyPrematureEnd: pathways.some((p) => p.endsMissing > 0),
  };
}

/**
 * The scoring aspect's control surface — the experience DEFAULT (mode + points + step-down, seeded onto
 * NEW tasks), the static point breakdown, and the two bulk remedies for the imbalance it flags: Apply to
 * Main Flow (stamp the default across the spine) and Clear Pathway Scores (zero every pathway to ungraded).
 * Rendered as a left inspector on wide screens and inside the bottom sheet in tablet-portrait/phone — same
 * component, both homes. Arming/firing of the two destructive bulk actions is owned by the parent (two-click).
 */
function ScoringPanel({
  tasks, scoringDefault, readOnly, onSetDefault,
  applyArmed, onApply, onApplyBlur, clearArmed, onClear, onClearBlur, split, pulse = 0,
  selectedCount = 0, onApplyToSelected,
}: {
  tasks: Task[];
  scoringDefault: ScoringDefault | undefined;
  readOnly: boolean;
  onSetDefault: (def: ScoringDefault) => void;
  applyArmed: boolean;
  onApply: () => void;
  onApplyBlur: () => void;
  clearArmed: boolean;
  onClear: () => void;
  onClearBlur: () => void;
  // Two-column layout (Default | overview) — phone landscape, where a tall single column doesn't fit.
  split?: boolean;
  /** Bumped by an undo/redo that moved the default, to ring the controls that hold it. */
  pulse?: number;
  /** How many board cards are lit. Clicking a card in this aspect picks it — see `activate`. */
  selectedCount?: number;
  onApplyToSelected?: () => void;
}) {
  const { t } = useTranslation();
  // The ring has to be taken away again, not just put up: with motion reduced `agavi-pulse` is a
  // held box-shadow rather than an animation, so an element left mounted rings forever.
  const [ringing, setRinging] = useState(false);
  useEffect(() => {
    if (!pulse) return;
    setRinging(true);
    const timer = setTimeout(() => setRinging(false), 700);
    return () => clearTimeout(timer);
  }, [pulse]);
  const def: ScoringDefault = scoringDefault ?? { mode: 'ungraded', points: 0, stepDown: 0 };
  const setMode = (mode: ScoringMode) => onSetDefault({
    mode,
    points: mode === 'ungraded' ? 0 : (def.points || 1),
    stepDown: mode === 'penalty' ? (def.stepDown || 1) : 0,
  });
  // How many wrong attempts bottom the task out — also the ladder's length, so it doubles as the
  // guard against rendering a ten-thousand-step list.
  const floorAt = def.mode === 'penalty'
    ? attemptsToFloor({ scoringMode: 'penalty', points: def.points, penalty: def.stepDown })
    : null;
  const { mainFlowTotal, pathways, hasPathways } = scoringBreakdown(tasks);
  const exceedCount = pathways.filter((pw) => pw.reachable && pw.total > 0).length;
  const endsCount = pathways.filter((pw) => pw.endsMissing > 0).length;

  return (
    <div className={`text-ws-text ${split ? 'grid grid-cols-2 items-start' : 'flex flex-col gap-4'}`}>
      {/* Default — seeded onto NEW tasks; Apply to Main Flow stamps it across the existing spine. */}
      <div className={`relative flex flex-col gap-2 ${split ? 'pr-5' : ''}`}>
        {/* Keyed on the pulse count so a second undo over the default restarts the animation
            rather than being a no-op on an element that is already there. Inset so the ring reads
            as belonging to these controls and not to the panel as a whole. */}
        {ringing && (
          <div key={pulse} aria-hidden='true'
            className='agavi-pulse pointer-events-none absolute -inset-2 rounded-lg' />
        )}
        <span className='text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('experience.scoring.default')}</span>
        <SegmentedControl
          className='mx-auto w-fit'
          aria-label={t('experience.scoring.default')}
          value={def.mode}
          onChange={(v) => setMode(v as ScoringMode)}
          options={[
            { id: 'ungraded', label: t('experience.scoring.mode.ungraded'), icon: <Ban aria-hidden='true' className='h-3.5 w-3.5' /> },
            { id: 'milestone', label: t('experience.scoring.mode.milestone'), icon: <Flag aria-hidden='true' className='h-3.5 w-3.5' /> },
            { id: 'penalty', label: t('experience.scoring.mode.penalty'), icon: <TrendingDown aria-hidden='true' className='h-3.5 w-3.5' /> },
          ]}
        />
        {def.mode !== 'ungraded' && (
          <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-0.5'>
            <ScoringStepper label={t('experience.scoring.pointsPerTask')} value={def.points} min={1} disabled={readOnly}
              // Lowering the points has to pull a now-too-large penalty down with it, or the pair
              // stops meaning what it shows. Same rule the service applies on the way in.
              onChange={(v) => onSetDefault({ ...def, points: v, stepDown: Math.min(def.stepDown, v) })} />
            {def.mode === 'penalty' && (
              <ScoringStepper label={t('experience.scoring.penaltyPerMistake')} value={def.stepDown} min={1} max={def.points} negative disabled={readOnly}
                onChange={(v) => onSetDefault({ ...def, stepDown: v })} />
            )}
          </div>
        )}
        {/* What the penalty actually does, spelled out. A teacher setting 5 points against a
            3-point penalty should be able to SEE `5 → 2 → 0` and decide whether that short last
            step is what they meant, rather than discovering it from a student's score.
            The full ladder only for a short one: 100 points against a 0.01 penalty is 10,001
            steps, so anything longer states the count instead of building the list. */}
        {def.mode === 'penalty' && floorAt !== null && floorAt > 0 && (
          <p className='text-center text-[11px] leading-snug text-ws-muted'>
            {floorAt <= LADDER_SHOWN
              ? <span className='tabular-nums'>{scoreLadder({ scoringMode: 'penalty', points: def.points, penalty: def.stepDown }).map(formatScore).join(' → ')}</span>
              : t('experience.scoring.reachesZero', { count: floorAt })}
          </p>
        )}
        {/* Primary at rest, armed amber on the first click — it stamps the default across every
            task on the spine, which is further than any other control in this panel reaches. */}
        <Button variant='primary' block className='mt-1' disabled={readOnly}
          armed={applyArmed} onClick={onApply} onBlur={onApplyBlur}>
          {applyArmed ? t('experience.scoring.applyAllConfirm') : t('experience.scoring.applyAll')}
        </Button>
        {/* The narrower sibling, and not armed: this reaches exactly what is lit on the board, and
            picking those cards was the confirmation. Shown only once something is picked, so the
            panel does not carry a dead control for the common case of scoring one task. */}
        {selectedCount > 0 && (
          <Button variant='secondary' block disabled={readOnly} onClick={onApplyToSelected}>
            {t('experience.scoring.applySelected', { count: selectedCount })}
          </Button>
        )}
      </div>

      {/* Scoring — per-lane point breakdown + Clear Pathway Scores (the rebalance action). Divider is a
          top border stacked, a left border split. */}
      <div className={`flex flex-col gap-2.5 border-ws-border ${split ? 'border-l pl-5' : 'border-t pt-3'}`}>
        <span className='text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('experience.tools.scoring')}</span>
        {/* Main Flow — the baseline every student attains. */}
        <div className='flex items-baseline justify-between gap-3 text-sm'>
          <span className='truncate font-medium text-ws-text'>{t('experience.builder.mainFlow')}</span>
          <span className='shrink-0 font-semibold tabular-nums text-ws-text'>{mainFlowTotal} {t('experience.scoring.pts')}</span>
        </div>
        {/* Pathways — a smaller sub-list, set apart from the baseline. Graded ones are flagged. */}
        {hasPathways && (
          <div className='flex flex-col gap-1 rounded-md border border-ws-border/60 bg-ws-bg/40 px-2.5 py-1.5'>
            <span className='text-[10px] font-semibold uppercase tracking-wide text-ws-muted/80'>{t('experience.builder.branches')}</span>
            {pathways.map((pw) => (
              <div key={pw.id} className='flex items-baseline justify-between gap-3 text-xs'>
                <span className='flex min-w-0 items-center gap-1'>
                  {/* TrendingUp = adds points on top of the main flow (earns MORE). */}
                  {pw.reachable && pw.total > 0 && (
                    <span className='shrink-0 text-ws-warning-fg' title={t('experience.scoring.pathwayExceedRow', { count: pw.total })}>
                      <TrendingUp role='img' aria-label={t('experience.scoring.pathwayExceedRow', { count: pw.total })} className='h-3 w-3' />
                    </span>
                  )}
                  {/* LogOut = ends without returning, so its students skip `endsMissing` pts of graded tail (earns LESS). */}
                  {pw.endsMissing > 0 && (
                    <span className='shrink-0 text-ws-warning-fg' title={t('experience.scoring.pathwayEndsRow', { count: pw.endsMissing })}>
                      <LogOut role='img' aria-label={t('experience.scoring.pathwayEndsRow', { count: pw.endsMissing })} className='h-3 w-3' />
                    </span>
                  )}
                  <span className={`truncate ${pw.name ? 'text-ws-muted' : 'italic text-ws-muted/70'}`}>{pw.name || t('experience.builder.pathwayUnnamed')}</span>
                </span>
                <span className={`shrink-0 tabular-nums ${pw.reachable && pw.total > 0 ? 'font-semibold text-ws-warning-fg' : 'text-ws-muted'}`}>{pw.total} {t('experience.scoring.pts')}</span>
              </div>
            ))}
          </div>
        )}
        {/* Earns MORE — graded pathways push the attainable total past the main-flow ceiling (TrendingUp). */}
        {exceedCount > 0 && (
          <span className='flex items-start gap-1.5 text-xs leading-snug text-ws-warning-fg'>
            <TrendingUp aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {t('experience.scoring.pathwayExceedWarning', { count: exceedCount })}
          </span>
        )}
        {/* Earns LESS — ending pathways make their students miss graded main-flow points (LogOut). */}
        {endsCount > 0 && (
          <span className='flex items-start gap-1.5 text-xs leading-snug text-ws-warning-fg'>
            <LogOut aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {t('experience.scoring.pathwayEndsWarning', { count: endsCount })}
          </span>
        )}
        {/* The first caller of `warning`: this clobbers every hand-graded pathway at once, and
            unlike Apply there is no default to re-derive them from. */}
        <Button variant='warning' block disabled={readOnly || !hasPathways}
          armed={clearArmed} onClick={onClear} onBlur={onClearBlur}
          icon={<Eraser aria-hidden='true' />}>
          {clearArmed ? t('experience.scoring.clearPathwaysConfirm') : t('experience.scoring.clearPathways')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Every snapshot entering the stack is normalized first.
 *
 * Applied HERE rather than at each of the nineteen `push` sites, because "each site remembers" is
 * precisely the bug: `pasteTasks` handled its own stranded `end` and every other way a task changes
 * position did not. `normalizeTerminals` returns the same array when nothing needs fixing, so this
 * costs one `deriveGraph` per edit and changes nothing in the common case.
 *
 * `reset` normalizes too, which heals data already stored that way — from an older build, a direct
 * API write, or another client — on the next save.
 */
const normalized = (tasks: Task[]): Task[] => normalizeTerminals(tasks);

function useHistory(initial: Task[]) {
  const [state, setState] = useState({ stack: [{ tasks: normalized(initial) }] as HistoryEntry[], cursor: 0 });

  const current = state.stack[state.cursor]?.tasks ?? initial;
  const currentDefault = state.stack[state.cursor]?.scoringDefault;

  /**
   * What the caller asked for, read back by the recorder effect below.
   *
   * Set from event handlers only — never during render — so the rule that a ref mutated in render
   * survives a discarded render does not apply here. The alternative, recording from inside the
   * state updater, is worse: an updater may run twice, and a diagnostic that double-logs every
   * step is actively misleading about the thing it was added to explain.
   */
  const intentRef = useRef<{ act: string; direction?: 'undo' | 'redo' } | null>(null);

  const push = useCallback((rawTasks: Task[]) => {
    intentRef.current = { act: 'push' };
    const tasks = normalized(rawTasks);
    setState((prev) => {
      // Bound the undo stack. Without a cap it grows one entry per edit for the whole session and
      // never shrinks (only `reset` on reload clears it) — a full day of editing accumulates
      // unboundedly, and any deep-cloned block data (image data-URIs from duplicate/paste) stays
      // pinned in every later entry. Drop the oldest entries past MAX_HISTORY and shift the cursor.
      // A task edit carries the current scoring default FORWARD (only pushDefault changes it).
      const stack = [...prev.stack.slice(0, prev.cursor + 1), { tasks, scoringDefault: prev.stack[prev.cursor]?.scoringDefault }];
      const cursor = prev.cursor + 1;
      const overflow = stack.length - MAX_HISTORY;
      return overflow > 0
        ? { stack: stack.slice(overflow), cursor: cursor - overflow }
        : { stack, cursor };
    });
  }, []);

  // Push a scoring-default change as its own undo step; tasks are carried forward unchanged.
  const pushDefault = useCallback((scoringDefault: ScoringDefault) => {
    intentRef.current = { act: 'pushDefault' };
    setState((prev) => {
      const tasks = prev.stack[prev.cursor]?.tasks ?? [];
      const stack = [...prev.stack.slice(0, prev.cursor + 1), { tasks, scoringDefault }];
      const cursor = prev.cursor + 1;
      const overflow = stack.length - MAX_HISTORY;
      return overflow > 0
        ? { stack: stack.slice(overflow), cursor: cursor - overflow }
        : { stack, cursor };
    });
  }, []);

  /**
   * What the last undo or redo moved, for the board to point at.
   *
   * A snapshot stack does not record what a step changed, so the two snapshots are compared when
   * the step is taken. `nonce` is what makes two identical steps distinguishable — undoing back
   * over the same task twice has to pulse twice, and an effect watching only `ids` would see no
   * change the second time.
   *
   * `scoringMoved` is separate from `ids` because the experience scoring default is not a task and
   * lives on the entry beside them. A step that only changes the default moves no task at all, so
   * a board watching `ids` alone showed nothing happening — which is exactly what an undo looks
   * like when it has failed.
   */
  const [lastStep, setLastStep] = useState<{ ids: string[]; scoringMoved: boolean; nonce: number }>(
    { ids: [], scoringMoved: false, nonce: 0 },
  );

  const step = useCallback((direction: -1 | 1) => {
    intentRef.current = { act: 'step', direction: direction === -1 ? 'undo' : 'redo' };
    setState((prev) => {
      const next = prev.cursor + direction;
      if (next < 0 || next > prev.stack.length - 1) {
        // A step off either end. It leaves no trace in `state`, so the effect below never sees it —
        // and "I pressed undo and nothing happened" is exactly the report this has to answer.
        queueMicrotask(() => recordUndoEvent('step', {
          surface: 'board', direction: direction === -1 ? 'undo' : 'redo',
          refused: 'at-end', depth: prev.stack.length, cursor: prev.cursor,
        }));
        return prev;
      }
      const from = prev.stack[prev.cursor]?.tasks ?? [];
      const to = prev.stack[next]?.tasks ?? [];
      const scoringMoved = JSON.stringify(prev.stack[prev.cursor]?.scoringDefault ?? null)
        !== JSON.stringify(prev.stack[next]?.scoringDefault ?? null);
      // Computed inside the updater, where both snapshots are to hand, but applied outside it: an
      // updater may run twice and must stay free of side effects.
      queueMicrotask(() => setLastStep((l) => ({ ids: diffBoard(from, to).ids, scoringMoved, nonce: l.nonce + 1 })));
      return { ...prev, cursor: next };
    });
  }, []);

  const undo = useCallback(() => step(-1), [step]);
  const redo = useCallback(() => step(1), [step]);

  const replace = useCallback((rawTasks: Task[]) => {
    intentRef.current = { act: 'replace' };
    const tasks = normalized(rawTasks);
    setState((prev) => ({
      ...prev,
      stack: prev.stack.map((entry, i) => (i === prev.cursor ? { tasks, scoringDefault: entry.scoringDefault } : entry)),
    }));
  }, []);

  const reset = useCallback((tasks: Task[], scoringDefault?: ScoringDefault) => {
    intentRef.current = { act: 'reset' };
    setState({ stack: [{ tasks: normalized(tasks), scoringDefault }], cursor: 0 });
  }, []);

  /**
   * The recorder, at the one seam every change to this stack passes through.
   *
   * Fires on COMMITTED state only, so each entry is a transition that really happened, once. What
   * it is looking for is the mismatch between what the stack did and what the board shows: a step
   * that moved the cursor while `changed: 'edited', ids: []` says the tasks are identical is an
   * undo that visibly did nothing, and a `push` with `dropped > 0` is a redo tail thrown away
   * under a teacher who was still stepping forward through it.
   */
  const prevStateRef = useRef(state);
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if (prev === state) return;
    const intent = intentRef.current;
    intentRef.current = null;
    const before = prev.stack[prev.cursor]?.tasks ?? [];
    const after = state.stack[state.cursor]?.tasks ?? [];
    const change = diffBoard(before, after);
    recordUndoEvent(intent?.act === 'step' ? 'step' : 'record', {
      surface: 'board',
      act: intent?.act ?? 'unknown',
      direction: intent?.direction,
      changed: change.kind,
      ids: change.ids.slice(0, 20),
      // The defect signature: the cursor moved and the tasks did not.
      cursorMoved: prev.cursor !== state.cursor,
      tasksMoved: change.ids.length > 0,
      // The redo tail this transition discarded, if any.
      dropped: intent?.act === 'push' || intent?.act === 'pushDefault'
        ? prev.stack.length - (prev.cursor + 1)
        : undefined,
      depth: state.stack.length,
      cursor: state.cursor,
      cursorBefore: prev.cursor,
      tasks: after.length,
    });
  }, [state]);

  return {
    tasks: current,
    scoringDefault: currentDefault,
    push,
    pushDefault,
    undo,
    redo,
    replace,
    reset,
    lastStep,
    canUndo: state.cursor > 0,
    canRedo: state.cursor < state.stack.length - 1,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

/** Tiny matchMedia hook for the builder's responsive bar/minibar layout. */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

/** Repair the SHAPE of any stored flow the board loads: a rule's `conditions` read as the flat
 * `FlowCondition[]` it is now (older rules hold the nested `Record<blockId, stateId[]>`, and some
 * hold nothing at all), plus the two lists the config grew. The backend's FlowRuleDto requires
 * each of these, so PATCHing an un-repaired rule back — which any flow edit on the task does —
 * 400s the whole request. Shape only: the board has no per-task blocks to work from, so the
 * blocks-aware half of the repair (sensor rows, condition labels, acknowledgements) waits for
 * normalizeFlowConfig when the task itself is opened.
 */
function normalizeTaskFlows(tasks: Task[]): Task[] {
  return tasks.map((tk) => {
    if (!tk.flow) return tk;
    const rules = tk.flow.eventRules ?? [];
    const migrated = rules.map((r) => ({ ...r, conditions: migrateConditionShape(r.conditions) }));
    /* Changes carry conditions too, and were missed here until a board holding seven of them
       across five experiences stopped loading at all. Same repair, same reason. */
    const changes = (Array.isArray(tk.flow.changes) ? tk.flow.changes : []).map((c) => ({ ...c, conditions: migrateConditionShape(c.conditions) }));
    // The sensor rungs are left to `normalizeFlowConfig`, which is the only place that knows which
    // blocks can report which kind of fault — and which also carries the pre-split `sensorRules`
    // migration. This pass repairs shapes it can judge on its own.
    const shapeChanged = migrated.some((r, i) => r.conditions !== rules[i]?.conditions)
      || changes.some((c, i) => c.conditions !== tk.flow!.changes?.[i]?.conditions)
      || !Array.isArray(tk.flow!.changes);
    if (!shapeChanged) return tk;
    return {
      ...tk,
      flow: {
        ...tk.flow,
        eventRules: migrated,
        changes,
      },
    };
  });
}

const errorsAspectKey = (experienceId: string) => `agavi.errorsAspect.${experienceId}`;

function readErrorsAspect(experienceId: string | undefined): boolean {
  if (!experienceId) return false;
  try { return sessionStorage.getItem(errorsAspectKey(experienceId)) === '1'; } catch { return false; }
}

export default function ExperiencePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const preview = usePreviewSession();
  const { t, i18n } = useTranslation();

  const isNew = id === 'new';
  const { readOnly, resume } = useTaskTakeOver(!isNew && id ? `experience-${id}` : undefined);
  const [searchParams] = useSearchParams();
  // Cutover: the new flow-graph builder is now the default. ?builder=0 still falls back to the old
  // canvas as an escape hatch while it lives; the old canvas + level/order/parentId are retired in a
  // follow-up once the default has proven itself in real use.
  const useNewBuilder = searchParams.get('builder') !== '0';

  const [experience, setExperience] = useState<Experience | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  const history = useHistory([]);

  /**
   * Preview from the builder starts at the BEGINNING, which is the only sensible answer here.
   *
   * The task editor's own Preview starts on the task you are looking at, because that is the one
   * you were working on. From the builder there is no such task -- you are looking at the whole
   * experience -- so it starts where a student would, at the head of the spine. Running the flow
   * from the top is also the thing the builder view exists to let you check.
   *
   * `deriveGraph().spine[0]` rather than `tasks[0]`, because the array holds detours too and the
   * first one in it need not be on the spine at all.
   */
  function startPreviewRun() {
    const first = deriveGraph(history.tasks).spine[0];
    if (!first || !id) return;
    preview.start(id);
    // See the task page: it reads its experience from `?experience=` and errors without it.
    navigate(`/app/task/${first}?experience=${id}`);
  }

  const canvas = useWorkspaceCanvas();
  const [canvasContainerSize, setCanvasContainerSize] = useState({ w: 0, h: 0 });
  const [canvasContentSize, setCanvasContentSize] = useState({ w: 0, h: 0 });

  const [deleteTask, setDeleteTask] = useState<Task | null>(null);
  const [showMiniMap, setShowMiniMap] = useState(false);
  // Floating canvas minibar stacks vertically when horizontal room is tight (portrait tablet/phone, or a
  // short landscape phone). Horizontal on desktop + tablet-landscape.
  const minibarVertical = useMediaQuery('(orientation: portrait) and (max-width: 1024px), (orientation: landscape) and (max-height: 500px)');
  // Phone-portrait / very narrow: no room on bar 1 alongside the title, so the tools move to a dedicated
  // SECOND bar (warnings · scoring · preview · collapse · expand · map) and the floating minibar is dropped.
  const narrowBars = useMediaQuery('(max-width: 640px)');
  // A finger, not a cursor. The selection bar's controls grow to a real touch target there — it is
  // the one surface in this mode where a mis-tap deletes something.
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  // Scoring aspect: toggled from the Scoring tool button; swaps card bodies to scoring controls.
  const [scoringAspect, setScoringAspect] = useState(false);
  /* The Errors aspect, a peer of Scoring: both disable the builder's wiring so the board can be
     read for one thing at a time, and only one may be on. */
  const [errorsAspect, setErrorsAspect] = useState(() => readErrorsAspect(id));
  /*
   * AND IT SURVIVES THE REPAIR TRIP.
   *
   * Fixing a problem means leaving for the task and coming back, which remounts this page; an
   * aspect that reset there asked the teacher to switch it on again after every single fix.
   * Per experience and per tab, because it describes what this teacher is doing right now, not
   * anything about the experience — a second tab reading the same board is free to differ.
   */
  useEffect(() => {
    if (!id) return;
    try { sessionStorage.setItem(errorsAspectKey(id), errorsAspect ? '1' : '0'); } catch { /* private mode */ }
  }, [id, errorsAspect]);

  /**
   * EVERY PROBLEM IN THE EXPERIENCE, recalculated rather than read back from anywhere.
   *
   * An issue is not a property of its block — it depends on experience variables, flow targets and
   * recalls into other tasks — so a stored copy is stale the moment anything else moves, and a
   * release that changes a check would leave every stored set wrong until each block was opened.
   * For a publication GATE that is fatal: it would report "publishable" about blocks nobody had
   * re-checked.
   *
   * Affordable because `experienceProblems` memoises per block on `block.data`'s identity, which a
   * write replaces wholesale — so an untouched board recomputes nothing.
   */
  /* The board reports a variable sitting in a block whose rung does not support one, and it is
     the surface that matters: such content usually arrives without an authoring action at all. */
  const { level } = useCapabilities();
  const variableRung = level('variables');
  const recallRung = level('answerRecall');

  const problems = useMemo(
    () => experienceProblems({
      tasks: history.tasks, variables: experience?.variables, t, locale: i18n.language,
      variableRung,
      /* Without these the board produces no recall rows at all, which is the state it shipped
         in: a dead reference was drawn on its block's card and never reached this list. */
      aliases: experience?.answerRefAliases ?? [],
      recallRung,
    }),
    [history.tasks, experience?.variables, experience?.answerRefAliases, t, i18n.language,
      variableRung, recallRung],
  );
  /* Grouped the way the panel lists them and summarised the way the cards draw them, both from the
     SAME set — so a mark on a card and a row in the panel can never disagree about a fault. */
  const problemGroups = useMemo(() => groupProblems(problems, history.tasks), [problems, history.tasks]);
  const problemsByTask = useMemo(() => summariseByTask(problems), [problems]);
  /**
   * The row the pointer is over, which the board previews: its task scrolls into view and the
   * block the row names lights up. Hover rather than a selection, because a selection made
   * reaching a problem a two-click job for a preview the pointer was already offering.
   */
  const [hoveredProblem, setHoveredProblem] = useState<ExperienceProblem | null>(null);
  /**
   * GO TO A PROBLEM — open the task it lives in, and name the block so the editor can reveal it.
   *
   * The block id travels as a query parameter rather than as router state, because a teacher who
   * copies the URL should land on the same control. A flow or task-level problem names no block
   * and simply opens the task.
   */
  const goToTask = useCallback((
    taskId: string,
    to: { blockId?: string; field?: string; exitSlot?: string; exitBlockId?: string; flowCardId?: string } = {},
  ) => {
    const q = new URLSearchParams({ experience: experience?.id ?? '' });
    if (to.blockId) q.set('block', to.blockId);
    /* The CONTROL, where the issue names one — `fields` is the `data-field-id` the check already
       found the fault in, so a row lands on the minimum rather than on the block holding it. */
    if (to.field) q.set('field', to.field);
    /*
     * SUMMARY FEEDBACK LIVES IN A MODAL, not on the canvas.
     *
     * Its blocks are authored on a flow EXIT, so there is no card to select and `block` would send
     * the task page hunting for an id that is in no task — the owner's "the click doesn't bring it
     * up". The slot travels instead, and the task page opens the flow panel and that exit's editor.
     */
    if (to.exitSlot) q.set('exit', to.exitSlot);
    /* And WHICH block inside it, so the dialog scrolls to it and flashes it rather than opening on
       a list of five and leaving the teacher to work out which one the row meant. */
    if (to.exitBlockId) q.set('exitBlock', to.exitBlockId);
    /*
     * A ROUTING FAULT LIVES ON A CARD IN THE FLOW PANEL, same as summary feedback lives in a
     * dialog: "a goto whose target was deleted" is not on the canvas at all. The card travels, and
     * the task page opens the panel, expands that card and scrolls to it.
     */
    if (to.flowCardId) q.set('flowCard', to.flowCardId);
    navigate(`/app/task/${taskId}?${q.toString()}`);
  }, [experience?.id, navigate]);

  const goToProblem = useCallback((problem: ExperienceProblem) => goToTask(problem.taskId, {
    blockId: problem.blockId, field: problem.issue.fields?.[0],
    exitSlot: problem.exitSlot, exitBlockId: problem.exitBlockId, flowCardId: problem.flowCardId,
  }), [goToTask]);
  /**
   * Select mode, and what is selected.
   *
   * A MODE rather than a gesture, because selection is scoping and scoping precedes choosing a verb
   * — and because the verbs it scopes are not all in one place. Copy/Duplicate/Delete live in the
   * card menu; the scoring verbs live in the scoring panel. Anything entered from one verb's menu
   * could only ever reach that menu's verbs, which would leave bulk scoring unreachable.
   *
   * Composes with the scoring aspect rather than excluding it: all four combinations mean something,
   * and Scoring + Select is the one that eventually retires "Apply to Main Flow".
   */
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deleteArmed, setDeleteArmed] = useState(false);
  const exitSelect = useCallback(() => { setSelectMode(false); setSelectedIds(new Set()); setDeleteArmed(false); }, []);

  /**
   * Select or clear a whole pathway from its bar.
   *
   * Copying a host together with the pathway hanging off it is the motivating case, and clicking
   * every member to get there is the thing that would make the mode not worth entering.
   */
  const togglePathwaySelected = useCallback((pathwayId: string) => {
    const members = history.tasks.filter((tk) => tk.pathwayId === pathwayId).map((tk) => tk.id);
    if (!members.length) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allIn = members.every((id) => next.has(id));
      for (const id of members) { if (allIn) next.delete(id); else next.add(id); }
      return next;
    });
  }, [history.tasks]);

  const toggleSelected = useCallback((taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(taskId)) next.add(taskId);
      return next;
    });
  }, []);
  // Bumped by an undo/redo that moved the experience scoring default, so the panel can ring. A
  // counter rather than a boolean: two undos in a row over the default must ring twice, and a
  // flag that is already `true` is not a change an effect can see.
  const [scoringPulse, setScoringPulse] = useState(0);
  const [scoringApplyArmed, setScoringApplyArmed] = useState(false); // two-click guard on Apply-to-all
  const [scoringClearArmed, setScoringClearArmed] = useState(false); // two-click guard on Clear-pathways
  // The scoring controls live in a LEFT inspector on wide screens; in tablet-portrait/phone that column
  // has no room beside the board, so they move into a modal instead (auto-opened on entering scoring;
  // reopenable from the board's floating Scoring button once dismissed).
  // Narrow (tablet-portrait/phone): the scoring panel is a bottom SHEET that peeks (headline only) so the
  // board stays fully editable, and expands on demand. Wide: it's the left inspector instead.
  /*
   * A SHEET SPENDS HEIGHT; AN INSPECTOR SPENDS WIDTH. Choose by which one is scarce.
   *
   * This asked about width alone, so phone LANDSCAPE — narrow by width, but with 390px of height
   * to its name — got the bottom sheet built for portrait. The owner found both halves of that:
   * scoring "pops up a little panel from the bottom ... but it also does this in landscape and
   * it's not really usable", and errors likewise "as a bottom bar, which is unusable".
   *
   * Landscape now takes the same floating inspector the desktop uses, which is the owner's own
   * reading: "in landscape, the panels should be on the left and mimic desktop, as we have
   * horizontal space to work with". It costs nothing in layout either way — both inspectors are
   * `absolute`, so they float OVER the board rather than reflowing it.
   */
  const shortLandscape = useMediaQuery('(orientation: landscape) and (max-height: 500px)');
  const scoringPanelAsSheet = useMediaQuery('(max-width: 1023px) and (orientation: portrait)');
  const [scoringSheetExpanded, setScoringSheetExpanded] = useState(false);
  // Drag-to-expand the sheet: a vertical drag on the peek beyond a small threshold sets expanded by
  // direction; a plain tap falls through to the click toggle (didDrag suppresses that click).
  const sheetDragStartY = useRef<number | null>(null);
  const sheetDidDrag = useRef(false);
  // Collapse the sheet back to its peek when leaving the scoring aspect (or when it stops being a sheet).
  useEffect(() => {
    if (!scoringAspect || !scoringPanelAsSheet) setScoringSheetExpanded(false);
  }, [scoringAspect, scoringPanelAsSheet]);

  // Leaving the scoring aspect drops what was picked there: the selection was for applying scoring,
  // and carrying it into the structure view would leave cards lit for no stated reason.
  useEffect(() => {
    if (!scoringAspect && !selectMode) setSelectedIds(new Set());
  }, [scoringAspect, selectMode]);
  // Escape collapses the expanded sheet back to its peek (it's not a modal, so this is a convenience,
  // not a focus trap — the board stays interactive underneath either way).
  useEffect(() => {
    if (!scoringSheetExpanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setScoringSheetExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [scoringSheetExpanded]);
  // New-builder overview map → jump: the board scrolls this task into view and pulses it.
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  /**
   * Moving focus and saying "look here" are different intentions, and conflating them is why
   * deleting a task flashed the one above it: the delete moves focus to the survivor so focus is
   * never stranded on `<body>` when the button unmounts, and that is housekeeping, not a pointer.
   * Only the callers that actually mean it set this.
   */
  // A list: one step can move many tasks (Apply to Main Flow stamps the whole spine in one entry),
  // and flashing only the card the viewport landed on understates what the step did.
  const [pulseTaskIds, setPulseTaskIds] = useState<string[] | null>(null);
  const focusAndPulse = useCallback((id: string) => { setFocusTaskId(id); setPulseTaskIds([id]); }, []);
  /** Scroll to the first, flash all of them. `ids` must already be in board order. */
  const focusAndPulseAll = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setFocusTaskId(ids[0]!);
    setPulseTaskIds(ids);
  }, []);

  /**
   * Undo and redo say what they did.
   *
   * The machinery to point at a task already existed for the overview map; undo simply never used
   * it, so a step moved, deleted or rewired something and the board gave no sign which. Pointing is
   * skipped when the changed task is not on the board — undoing an ADD removes it, and there is
   * nothing to show.
   */
  useEffect(() => {
    if (!history.lastStep.nonce) return;
    // Every task the step moved, not just the first — see `tasksToPointAt`.
    const ids = tasksToPointAt({ kind: 'edited', ids: history.lastStep.ids }, history.tasks);
    focusAndPulseAll(ids);
    // A step that moved the experience scoring default changed something that is not a task, so
    // the board has nothing to flash. Show it where it actually lives: open the scoring panel if
    // it is shut — undoing into a closed panel is the same "nothing happened" as not undoing at
    // all — and ring it. Same rule the task builder settled on for its options tray: reveal the
    // surface that owns what moved.
    // The step may have removed tasks the selection named, so it does not survive one.
    if (selectMode) exitSelect();
    if (history.lastStep.scoringMoved) {
      setScoringAspect(true);
      setScoringPulse((n) => n + 1);
    }
    // The other half of "undo did nothing": the stack may have moved data while the board pointed
    // at nothing, either because the step named no task or because the ones it named are off-board.
    recordUndoEvent('reveal', {
      surface: 'board', pointedAt: ids[0] ?? null, pulsed: ids.length,
      scoringMoved: history.lastStep.scoringMoved,
      named: history.lastStep.ids.slice(0, 20),
      onBoard: history.tasks.length,
    });
    // Keyed on the nonce alone: two undos over the same task must pulse twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.lastStep.nonce]);
  /**
   * Cmd/Ctrl+Z and its redo twin, which this page did not have at all — undo here was reachable
   * only by mouse, while the task builder has had the shortcut all along. Same guards as there: a
   * field that is being typed into owns its own undo, and a modifier is required.
   */
  const undoRef = useRef(() => {});
  const redoRef = useRef(() => {});
  const exitSelectRef = useRef(() => {});
  const copySelectionRef = useRef(() => {});
  const pasteRef = useRef(() => {});
  undoRef.current = () => { if (!readOnly) history.undo(); };
  redoRef.current = () => { if (!readOnly) history.redo(); };
  exitSelectRef.current = () => { if (selectMode) exitSelect(); };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      const shortcut = selectionShortcut(e);
      if (!shortcut) return; // a bare modifier, a named key, or a plain letter — see `selectionShortcut`
      // Copy closes the mode ITSELF, and must run before anything clears the selection it copies.
      if (shortcut === 'copy') { e.preventDefault(); copySelectionRef.current(); return; }
      // Everything else ends the mode first: its affordances are suppressed while the mode is on,
      // so leaving the pill up would put it in front of the thing the shortcut is for.
      exitSelectRef.current();
      if (shortcut === 'paste') { e.preventDefault(); pasteRef.current(); return; }
      if (shortcut === 'undo') { e.preventDefault(); undoRef.current(); }
      if (shortcut === 'redo') { e.preventDefault(); redoRef.current(); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Overview map → hover: ring the matching board card while the pointer is over its block.
  const [mapHoverTaskId, setMapHoverTaskId] = useState<string | null>(null);
  // The board's scroll container, shared with the overview map so it can draw + drag the viewport box.
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  /** What the guarded exit will DO — a thunk, not a URL, because logout clears the token on
   *  its way out and must only do so if the teacher really leaves. */
  const pendingExitRef = useRef<(() => void) | null>(null);
  const savedSnapshotRef = useRef<string>('');
  // Which task ids the server already knows about — lets persistTasks omit content
  // (blocks/variables/flow) for those, sending it only for genuinely new tasks.
  // See persistTasks's own comment for why this isn't optional.
  const knownServerTaskIdsRef = useRef<Set<string>>(new Set());
  // What flow the SERVER currently holds for each task (JSON), so the structural autosave can tell
  // when a local flow diverged (e.g. the user hit Undo on a wire) and re-push it via the flow-only
  // endpoint. Without this, undo reverts local flow but the server keeps the old wire, and the next
  // structural reconcile pulls the server's still-connected flow back in — the wire "reappears".
  const syncedFlowRef = useRef<Map<string, string>>(new Map());
  // The most recent in-flight persistTasks promise (null once settled). handleEditTask awaits it so
  // it never navigates into the task editor — a separate route that re-fetches from the server —
  // before a just-created task's PATCH has landed. See handleEditTask's own comment.
  const savingPromiseRef = useRef<Promise<Experience | null> | null>(null);
  // Reorder mode was toggled from the (now-removed) bottom actions bar; the old canvas still reads
  // it, but with no toggle it stays off. Retired with the old canvas at cutover cleanup.
  const isReorderMode = false;

  const creating = useRef(false);

  // ── Load or create on mount ─────────────────────────────────────────────

  useEffect(() => {
    if (isNew) {
      // Auto-create and redirect to the real ID
      if (creating.current) return;
      creating.current = true;

      api
        .post<Experience>('/experiences', {})
        .then((exp) => {
          navigate(`/app/experience/${exp.id}`, { replace: true });
        })
        .catch(() => {
          setError(t('experience.errors.couldNotCreate'));
          setLoading(false);
        });
      return;
    }

    if (!id) return;

    // React StrictMode (dev) double-invokes this effect, and a remount does the same in production;
    // the two GETs are in flight together with no ordering guarantee. Without this guard the request
    // that SETTLES last wins regardless of which fired first, so a replayed request that fails
    // overwrites state the other one already loaded correctly. The task editor hit exactly this and
    // guards it the same way.
    let cancelled = false;
    // Which side of the `await` a throw came from. Everything past the request itself — normalizing
    // flows, resetting history, snapshotting — runs inside the `.then()`, where a throw lands in the
    // same `.catch()` as a dead network. Conflating them let a client-side bug print "Experience not
    // found" about an experience that had just been fetched successfully.
    let loaded = false;
    recordLoadEvent('mount', { page: 'board', experienceId: id });
    setLoading(true);
    api
      .get<Experience>(`/experiences/${id}`)
      .then((exp) => {
        if (cancelled) {
          recordLoadEvent('load', { page: 'board', phase: 'discarded', ...sampleIds(exp.tasks.map((x) => x.id)) });
          return;
        }
        loaded = true;
        recordLoadEvent('load', { page: 'board', phase: 'ok', ...sampleIds(exp.tasks.map((x) => x.id)) });
        const tasks = normalizeTaskFlows(exp.tasks);
        setExperience({ ...exp, tasks });
        // `error` is never otherwise cleared, so one transient failure kept the error screen up for
        // the life of the mount — every later successful load rendered underneath it. That is what
        // "it keeps saying experience not found" was: the first failure, still on screen.
        setError('');
        history.reset(tasks, exp.scoringDefault);
        savedSnapshotRef.current = JSON.stringify({ title: exp.title, tasks });
        knownServerTaskIdsRef.current = new Set(tasks.map((t) => t.id));
        syncedFlowRef.current = new Map(tasks.map((t) => [t.id, JSON.stringify(t.flow ?? DEFAULT_FLOW_CONFIG)]));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // The message is a summary; the cause is what anyone debugging this actually needs, and it
        // was being swallowed entirely.
        console.error('[experience] load failed', e);
        recordLoadEvent('load', { page: 'board', phase: loaded ? 'apply-threw' : 'fetch-failed', error: describeError(e) });
        if (loaded) setError(t('experience.errors.somethingWrong'));
        else if (classifyLoadFailure(e) === 'notFound') setError(t('experience.errors.notFound'));
        else setError(t('experience.errors.couldNotLoad'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // What the board settled on. From an effect, not from the render that decides it — a discarded
  // render would otherwise log a screen nobody ever saw.
  useEffect(() => {
    if (loading || (!error && experience)) return;
    recordLoadEvent('screen', {
      page: 'board', showing: 'error', experienceId: id,
      why: error ? 'error-set' : 'no-experience',
      message: error || undefined,
    });
  }, [loading, error, experience, id]);

  // ── Helpers ─────────────────────────────────────────────────────────────

  const handleCreateTask = useCallback(() => {
    if (readOnly) return;
    const nextLevel =
      history.tasks.length === 0
        ? 1
        : Math.max(...history.tasks.map((t) => t.level)) + 1;
    const newTask: Task = {
      id: `tsk_${crypto.randomUUID()}`,
      title: 'New Task',
      level: nextLevel,
      order: 0,
      version: 1,
      createdAt: new Date().toISOString(),
      ...newTaskScoring(experience?.scoringDefault, false),
      blocks: [],
    };
    const newTasks = [...history.tasks, newTask];
    history.push(newTasks);
    setFocusTaskId(newTask.id); // move keyboard focus onto the new card (not left on the add button)
    // Persist immediately, don't wait for the debounce — a teacher could plausibly click
    // straight into this task's editor before the 1s timer would otherwise fire, which would
    // hit "task not found" (the editor is a separate route that re-fetches independently).
    void persistTasks(newTasks);
  }, [history.tasks, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // New builder: create a task as a detour (remediation) off `parentTaskId` — pathwayOf makes it a
  // task in that task's redirect column. Appended to the array so it stacks last in its parent's
  // lane; deriveGraph reads pathwayOf + array order (level/order are only kept for the old canvas).
  const handleInsertAt = useCallback(
    (targetLevel: number) => {
      if (readOnly) return;
      const newTask: Task = {
        id: `tsk_${crypto.randomUUID()}`,
        title: t('experience.newTask'),
        level: targetLevel,
        order: 0,
        version: 1,
        createdAt: new Date().toISOString(),
        ...newTaskScoring(experience?.scoringDefault, false),
        blocks: [],
      };
      const newTasks = [
        ...history.tasks.map((t) => (t.level >= targetLevel ? { ...t, level: t.level + 1 } : t)),
        newTask,
      ];
      history.push(newTasks);
      void persistTasks(newTasks);
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Legacy canvas path: open the confirm modal. The new builder confirms in-menu (two-click), so it
  // uses handleDeleteTaskDirect instead — no modal.
  const handleDeleteTask = useCallback(
    (taskId: string) => {
      if (readOnly) return;
      const task = history.tasks.find((t) => t.id === taskId);
      if (task) setDeleteTask(task);
    },
    [history.tasks, readOnly],
  );
  // Direct single-task delete (no modal) — the builder's ⋯ menu is the confirm (two-click arm). Cuts
  // any goto that pointed at the deleted task, same as handleConfirmDelete.
  const handleDeleteTaskDirect = useCallback(
    (taskId: string) => {
      if (readOnly) return;
      if (history.tasks.some((t) => t.pathwayOf === taskId)) return; // hosts pathways → blocked (would orphan them)
      // Pick a neighbour to receive keyboard focus after the card unmounts (else focus falls to body):
      // the same-lane task just before it, else just after, else its host, else the first task left.
      const idx = history.tasks.findIndex((t) => t.id === taskId);
      const self = history.tasks[idx];
      const laneKey = (t: Task | undefined) => t?.pathwayOf ?? '__spine';
      const key = laneKey(self);
      const before = [...history.tasks.slice(0, idx)].reverse().find((t) => laneKey(t) === key);
      const after = history.tasks.slice(idx + 1).find((t) => laneKey(t) === key);
      const neighbour = before ?? after ?? (self?.pathwayOf ? history.tasks.find((t) => t.id === self.pathwayOf) : undefined) ?? history.tasks.find((t) => t.id !== taskId);
      const gone = new Set([taskId]);
      const remaining = history.tasks
        .filter((t) => t.id !== taskId)
        .map((t) => (t.flow && flowGotoTargets(t.flow).includes(taskId) ? { ...t, flow: cutFlowTargeting(t.flow, gone) } : t));
      history.push(remaining);
      if (neighbour) setFocusTaskId(neighbour.id);
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Delete a whole pathway: drop every member task and cut any goto that pointed AT one (same
  // fall-through revert as a single delete). Immediate + undoable, like the other pathway actions.
  const handleDeletePathway = useCallback(
    (pathwayId: string) => {
      if (readOnly) return;
      const gone = new Set(history.tasks.filter((t) => t.pathwayId === pathwayId).map((t) => t.id));
      if (!gone.size) return;
      // Blocked if a member hosts a nested pathway (a task outside this one hangs off a member).
      if (history.tasks.some((t) => t.pathwayOf && gone.has(t.pathwayOf) && !gone.has(t.id))) return;
      const host = history.tasks.find((t) => t.pathwayId === pathwayId)?.pathwayOf; // land focus on the host
      const remaining = history.tasks
        .filter((t) => !gone.has(t.id))
        .map((t) => (t.flow && flowGotoTargets(t.flow).some((target) => gone.has(target)) ? { ...t, flow: cutFlowTargeting(t.flow, gone) } : t));
      history.push(remaining);
      if (host && remaining.some((t) => t.id === host)) setFocusTaskId(host);
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleConfirmDelete = useCallback(() => {
    if (readOnly || !deleteTask) return;
    const taskId = deleteTask.id;
    setDeleteTask(null);
    const gone = new Set([taskId]);
    // Also cut any goto that pointed AT the deleted task, reverting it to fall-through — otherwise
    // that event stays wired to a target that no longer exists and gets stuck "unavailable" forever
    // (it can't be re-linked). The reverted flow persists via the autosave's persistFlowDiffs.
    const remaining = history.tasks
      .filter((t) => t.id !== taskId)
      .map((t) => (t.flow && flowGotoTargets(t.flow).includes(taskId) ? { ...t, flow: cutFlowTargeting(t.flow, gone) } : t));
    history.push(remaining);
  }, [deleteTask, history.tasks, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Copy and paste tasks ───────────────────────────────────────────────────

  const [clipboard, setClipboard] = useState(() => readClipboard());
  useEffect(() => subscribeClipboard(() => setClipboard(readClipboard())), []);

  /** What the clipboard holds, if it holds tasks. A block copy is not something a board can take. */
  /** Pathways every one of whose members is selected — what lets the bar show lit rather than half-lit. */
  const selectedPathwayIds = useMemo(() => {
    const byPathway = new Map<string, string[]>();
    for (const tk of history.tasks) {
      if (!tk.pathwayId) continue;
      (byPathway.get(tk.pathwayId) ?? byPathway.set(tk.pathwayId, []).get(tk.pathwayId)!).push(tk.id);
    }
    const full = new Set<string>();
    for (const [pw, ids] of byPathway) if (ids.every((id) => selectedIds.has(id))) full.add(pw);
    return full;
  }, [history.tasks, selectedIds]);

  const clipboardTasks = clipboard?.kind === 'tasks' ? clipboard.tasks : null;
  // How deep the clipboard set is inside itself, so the board can tell which hosts it would still
  // fit under. Flat for a single task; 1+ for a pathway that contains a branch of its own.
  const clipboardDepth = useMemo(
    () => internalDepth((clipboardTasks ?? []) as unknown as { id: string; pathwayOf?: string }[]),
    [clipboardTasks],
  );

  /** Everything the paste endpoint needs, taken as a snapshot so the original can change or go. */
  /** A paste held open while the teacher decides what to bring with it. See `pasteTasksChecked`. */
  const [pendingPull, setPendingPull] = useState<{
    rows: PullRow[];
    /** The SOURCE experience's tasks, to snapshot the chosen ones from. */
    sourceTasks: Task[];
    /** And its aliases and experience variables: a pulled task needs both, and neither is on
     *  the clipboard, which only ever carried what the teacher selected. */
    sourceAliases: AnswerRefAlias[];
    sourceVariables: TaskVariable[];
    tasks: ClipboardTask[];
    where: { afterTaskId?: string; asBranchOfTaskId?: string };
  } | null>(null);

  // `snapshotTask` lives in `lib/clipboard` because pull-through takes the same snapshot from a
  // different experience on another surface, and two ideas of what a copied task is would drift.
  const toClipboardTask = useCallback(
    // The experience's variables go on the clipboard too, narrowed to what this task reaches.
    // A task pasted into another experience has nothing to resolve `{river}` against otherwise,
    // and the clipboard is a snapshot — by the time it is pasted the source may be gone.
    (task: Task): ClipboardTask => snapshotTask(task, experience?.variables ?? []),
    [experience],
  );

  /**
   * The aliases a set of copied tasks' chips name, for the clipboard.
   *
   * Only the ones they NAME: copying a task should not drag the experience's whole vocabulary
   * behind it. The server narrows again on arrival, so this is about what the clipboard carries
   * rather than about correctness.
   */
  const aliasesFor = useCallback((tasks: { id: string }[]): AnswerRefAlias[] => {
    const all = experience?.answerRefAliases ?? [];
    if (all.length === 0) return [];
    const ids = new Set(tasks.map(tk => tk.id));
    const used = new Set(
      collectAliasUsages((experience?.tasks ?? []) as never)
        .filter(u => ids.has(u.inTaskId))
        .map(u => u.aliasId),
    );
    return all.filter(a => used.has(a.id));
  }, [experience]);

  const handleCopyTask = useCallback((taskId: string) => {
    const task = history.tasks.find((tk) => tk.id === taskId);
    if (!task || !experience) return;
    // A lone task is a root — stated rather than left absent, so every clipboard payload has the
    // same shape whether it came from one card or a selection.
    writeClipboard({
      kind: 'tasks',
      source: { experienceId: experience.id, taskId },
      tasks: [{ ...toClipboardTask(task), pathwayOf: null }],
      aliases: aliasesFor([task]),
    });
    setClipboard(readClipboard());
    // Pulse the card instead of a toast. Every other toast in this app reports a failure, so using
    // one to confirm an ordinary action makes the two indistinguishable at a glance — and copying
    // is not worth a modal-weight interruption. The builder announces it for screen readers.
    focusAndPulse(taskId);
  }, [history.tasks, experience, toClipboardTask, focusAndPulse, aliasesFor]);

  /**
   * Copy a whole pathway: every member, in board order.
   *
   * Members only — the host the pathway hangs off does not travel. A pasted pathway is re-hosted
   * onto whichever task it lands on, so carrying the old host would be carrying the one fact the
   * paste is about to replace.
   *
   * Order matters and is taken from `history.tasks` rather than from the pathway: the paste
   * endpoint mints ids for the set up front and lays the members out in array order, so the copy's
   * sequence is this array's sequence.
   */
  const handleCopyPathway = useCallback((pathwayId: string) => {
    if (!experience) return;
    const members = history.tasks.filter((tk) => tk.pathwayId === pathwayId);
    if (!members.length) return;
    writeClipboard({
      kind: 'tasks',
      source: { experienceId: experience.id, taskId: members[0]!.id },
      tasks: members.map(toClipboardTask),
      aliases: aliasesFor(members),
    });
    setClipboard(readClipboard());
    // Same confirmation as copying a task: pulse the head rather than raise a toast, since every
    // other toast in this app reports a failure.
    focusAndPulse(members[0]!.id);
  }, [history.tasks, experience, toClipboardTask, focusAndPulse, aliasesFor]);

  /**
   * Copy the current selection, in board order.
   *
   * Order comes from `history.tasks`, not from the selection, because the paste endpoint lays a set
   * out in array order — so what you see top-to-bottom is what arrives top-to-bottom.
   *
   * Each member's `pathwayOf` is resolved against the SELECTION before it reaches the clipboard: a
   * sparse pick can skip a layer, and only the board can climb past the gap. See
   * `resolveSelectionParents`.
   */
  const handleCopySelection = useCallback(() => {
    if (!experience || !selectedIds.size) return;
    const members = history.tasks.filter((tk) => selectedIds.has(tk.id));
    if (!members.length) return;
    const parents = resolveSelectionParents(history.tasks, selectedIds);
    writeClipboard({
      kind: 'tasks',
      source: { experienceId: experience.id, taskId: members[0]!.id },
      tasks: members.map((m) => ({ ...toClipboardTask(m), pathwayOf: parents.get(m.id) ?? null })),
      aliases: aliasesFor(members),
    });
    setClipboard(readClipboard());
    focusAndPulseAll(members.map((m) => m.id));
    exitSelect();
  }, [experience, selectedIds, history.tasks, toClipboardTask, focusAndPulseAll, exitSelect, aliasesFor]);

  const pasteTasksInto = useCallback(async (
    tasks: ClipboardTask[],
    where: { afterTaskId?: string; asBranchOfTaskId?: string },
    /**
     * Tasks to bring because the copied set's references point at them, already snapshotted
     * from the source experience. They go in FRONT of the copy, at the same anchor: array
     * order within a lane is the sequence, so that is what makes the answer reached before the
     * question citing it.
     */
    pulled: ClipboardTask[] = [],
    /** The aliases those pulled tasks' own chips name — see `withPulledAliases`. */
    pulledAliases: AnswerRefAlias[] = [],
  ) => {
    if (readOnly || !experience) return;
    setSaving(true);
    try {
      /*
       * Content is dropped when it is already on the server.
       *
       * A selection pastes N tasks, and N times the blocks, variables and flow was enough to exceed
       * the request body limit and fail the whole paste with a 500 — reported as pasting several
       * tasks "just gave an error". Pasting back into the experience a task came FROM (which is
       * every Duplicate, and most Copy) needs only the ids: the server holds the content under
       * `sourceId`. Same rule `persistTasks` already follows for tasks the server knows about.
       */
      const sameDocument = isSameDocumentPaste(tasks, new Set(history.tasks.map((tk) => tk.id)));
      const payload = sameDocument
        ? tasks.map(({ blocks: _b, variables: _v, flow: _f, experienceVariables: _ev, ...rest }) => rest)
        // Pulled tasks ALWAYS carry their content: they come from the other document, so there
        // is nothing here for the server to hydrate them from.
        //
        // And the experience variables each one carried become its OWN, because the scope they
        // belonged to does not exist here. Only on this branch: folding them on a same-document
        // paste would turn a per-session value into a per-task one for no reason, since the
        // name already resolves. See `foldExperienceVariables`.
        : [...pulled, ...tasks].map(foldExperienceVariables);
      const before = new Set(history.tasks.map((tk) => tk.id));
      const updated = await api.post<Experience>(
        // Aliases always travel, even on a same-document paste where the content does not:
        // they are small, and the server skips any this experience already holds.
        `/experiences/${experience.id}/tasks/paste`,
        {
          tasks: payload,
          aliases: withPulledAliases(
            readClipboard()?.kind === 'tasks' ? (readClipboard() as ClipboardTasks).aliases ?? [] : [],
            pulled,
            // Already narrowed by the caller to the chosen tasks' own aliases; passing them
            // again as the pool is what lets this stay one function for both paste surfaces.
            pulledAliases,
          ),
          ...where,
        },
      );
      setExperience(updated);
      history.push(updated.tasks);
      // Point at what arrived. A duplicate lands after the LAST selected task, which for a task
      // with several pathways is far enough down the board to be off-screen — the paste looked
      // like it had done nothing at all.
      const arrived = updated.tasks.filter((tk) => !before.has(tk.id)).map((tk) => tk.id);
      if (arrived.length) focusAndPulseAll(arrived);
      // The server made these, so a later structural PATCH must not re-send their content.
      knownServerTaskIdsRef.current = new Set(updated.tasks.map((tk) => tk.id));
      // And the flow-sync pass must not re-send their FLOW: without this it saw every pasted task's
      // flow as locally changed and PATCHed it straight back, which failed validation and surfaced
      // as "couldn't save" one second after a paste that had in fact succeeded.
      syncedFlowRef.current = new Map(
        updated.tasks.map((tk) => [tk.id, JSON.stringify(tk.flow ?? DEFAULT_FLOW_CONFIG)]),
      );
    } catch {
      setSaveFailed(true);
      setToastMsg(t('experience.errors.couldNotSave'));
    } finally {
      setSaving(false);
    }
  }, [experience, readOnly, t, focusAndPulseAll]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * THE GATE EVERY CROSS-EXPERIENCE TASK PASTE GOES THROUGH.
   *
   * A copied task's chips name answers that live in OTHER tasks. Pasted into an experience
   * that has none of them, every one of those references is dead on arrival. So before the
   * paste, work out which tasks would have to come too, and ask.
   *
   * It asks only when there is something to ask about, which is the same silence rule the
   * scenario-set paste follows: a Duplicate, and any paste back into the experience a task
   * came from, finds its answers already present and behaves exactly as it did before.
   *
   * A failed lookup pastes anyway. Being unable to OFFER the pull is not a reason to refuse
   * the paste the teacher asked for, and the result is the state that shipped before this:
   * a named, repointable, broken reference.
   */
  const pasteTasksChecked = useCallback(async (
    tasks: ClipboardTask[],
    where: { afterTaskId?: string; asBranchOfTaskId?: string },
  ) => {
    const payload = readClipboard();
    const sourceId = payload?.source.experienceId;
    const go = () => { void pasteTasksInto(tasks, where); };
    if (!experience || !sourceId || sourceId === experience.id) { go(); return; }
    if (payload?.kind !== 'tasks' || !(payload.aliases ?? []).length) { go(); return; }

    let source: Experience;
    try { source = await api.get<Experience>(`/experiences/${sourceId}`); }
    catch { go(); return; }

    const rows = pullRows({
      sourceTasks: source.tasks ?? [],
      sourceAliases: source.answerRefAliases ?? [],
      seedTaskIds: tasks.map(tk => tk.sourceId),
      alreadyHere: alreadyHereIds(experience.tasks ?? []),
    });
    if (!rows.length) { go(); return; }
    setPendingPull({ rows, sourceTasks: source.tasks ?? [], tasks, where,
      sourceAliases: source.answerRefAliases ?? [], sourceVariables: source.variables ?? [] });
  }, [experience, pasteTasksInto]);

  const handlePasteTasks = useCallback((afterTaskId?: string) => {
    const payload = readClipboard();
    if (payload?.kind !== 'tasks') { setClipboard(payload); return; }
    void pasteTasksChecked(payload.tasks, { afterTaskId });
  }, [pasteTasksChecked]);

  /*
   * The keyboard's view of Copy and Paste, assigned here because the handlers are declared above
   * this point and the key listener is installed once, far earlier.
   */
  // Cmd/Ctrl+C copies the selection — a no-op outside select mode, where nothing is selected.
  copySelectionRef.current = () => { if (selectMode && selectedIds.size) handleCopySelection(); };
  // Cmd/Ctrl+V appends to the end of the spine. The board has no cursor, so where a paste lands has
  // to be a stated place rather than an inferred one, and the End cap's New Task appends too.
  pasteRef.current = () => { if (!readOnly && clipboard?.kind === 'tasks') handlePasteTasks(); };

  /** Duplicate the selection in place: the same copy the clipboard would carry, pasted where it sits. */
  const handleDuplicateSelection = useCallback(() => {
    if (!experience || !selectedIds.size) return;
    const members = history.tasks.filter((tk) => selectedIds.has(tk.id));
    if (!members.length) return;
    const parents = resolveSelectionParents(history.tasks, selectedIds);
    // The last selected ROOT — see `duplicateAnchorId` for why it cannot be the last selected task.
    const afterTaskId = duplicateAnchorId(members, parents);
    void pasteTasksInto(
      members.map((m) => ({ ...toClipboardTask(m), pathwayOf: parents.get(m.id) ?? null })),
      { afterTaskId },
    );
    exitSelect();
  }, [experience, selectedIds, history.tasks, toClipboardTask, pasteTasksInto, exitSelect]);

  /** See `orphanedHosts`: what a delete would strand, and therefore what it refuses. */
  const orphaningHosts = useMemo(
    () => orphanedHosts(history.tasks, selectedIds),
    [history.tasks, selectedIds],
  );

  /** Delete everything selected, in one undo step. Refused while it would orphan a pathway. */
  const handleDeleteSelection = useCallback(() => {
    if (readOnly || !selectedIds.size || orphaningHosts.size) return;
    const gone = new Set(selectedIds);
    const remaining = history.tasks
      .filter((t) => !gone.has(t.id))
      .map((t) => (t.flow && flowGotoTargets(t.flow).some((id) => gone.has(id)) ? { ...t, flow: cutFlowTargeting(t.flow, gone) } : t));
    history.push(remaining);
    void persistTasks(remaining);
    exitSelect();
  }, [readOnly, selectedIds, history.tasks, orphaningHosts, exitSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The one write path for putting a task into this experience, whatever asked for it.
   *
   * Server-side because it is a cross-document create: ids regenerate, Flow is remapped against the
   * pasted set, gotos that point outside it become loose threads, and `allCorrectAction` is
   * re-derived from where the task lands. Doing it here would mean a second implementation of rules
   * the server already has — which is exactly how `duplicateTask` came to drop Flow while
   * `duplicateInto` kept it.
   */

  /**
   * Paste the clipboard as a NEW pathway hanging off `hostTaskId`.
   *
   * Arrives UNWIRED, the same as a branch made by dragging a card onto another: `pathwayOf` is set
   * and no flow event points at it yet, so the board flags it "no entrance" until the teacher wires
   * it. That is the honest state — nothing here can know which of the host's events was meant.
   *
   * Refused past the soft depth cap rather than offered and then failed. The callers do not render
   * the action in that case either; this is the guard for anything that reaches the handler anyway.
   */
  const handlePastePathwayOnto = useCallback((hostTaskId: string) => {
    const payload = readClipboard();
    if (payload?.kind !== 'tasks') { setClipboard(payload); return; }
    if (!fitsBranchDepth(branchDepth(history.tasks, hostTaskId), payload.tasks as unknown as { id: string; pathwayOf?: string }[])) return;
    void pasteTasksChecked(payload.tasks, { asBranchOfTaskId: hostTaskId });
  }, [pasteTasksChecked, history.tasks]);

  /**
   * Duplicate is copy + paste-here — the same server path, not a second implementation.
   *
   * It used to clone the task in the browser and DELIBERATELY drop its Flow, because regenerating
   * block ids left every condition pointing at blocks that no longer existed. The server already
   * solved that for whole-experience duplication by remapping through the same id maps, so routing
   * through paste means a duplicated task keeps its branching instead of silently losing it — and
   * there is no longer a second copy of these rules to drift from the first.
   */
  const handleDuplicateTask = useCallback(
    (taskId: string) => {
      if (readOnly) return;
      const original = history.tasks.find((tk) => tk.id === taskId);
      if (!original) return;
      void pasteTasksInto(
        [{ ...toClipboardTask(original), title: t('common.copyOf', { name: original.title }) }],
        { afterTaskId: taskId },
      );
    },
    [history.tasks, readOnly, pasteTasksInto, toClipboardTask, t],
  );

  const handleEditTask = useCallback(
    async (task: Task) => {
      // The task editor is a SEPARATE route that re-fetches the experience from the server on
      // mount, so the clicked task must already exist server-side or it renders "task not found"
      // until a manual refresh. Flush anything not-yet-persisted before navigating:
      //  • create/duplicate/insert fire an immediate persist that may still be in flight, and
      //  • a reorder/points edit only reaches the server via the 1s debounced autosave.
      if (!readOnly && experience) {
        if (savingPromiseRef.current) await savingPromiseRef.current; // let an in-flight save land
        // knownServerTaskIdsRef + savedSnapshotRef are updated by persistTasks (live refs, not a
        // stale closure), so this re-check sees the just-landed save; only persist again if there's
        // still unsaved work or the clicked task somehow still isn't on the server.
        const dirty = JSON.stringify({ title: experience.title, tasks: history.tasks }) !== savedSnapshotRef.current;
        if (dirty || !knownServerTaskIdsRef.current.has(task.id)) await persistTasks(history.tasks);
        // If the task still isn't on the server (the save failed — persistTasks has already shown
        // the "couldn't save" toast), don't navigate into a dead-end "task not found" screen.
        if (!knownServerTaskIdsRef.current.has(task.id)) {
          recordLoadEvent('note', { page: 'board', what: 'edit-blocked', taskId: task.id, dirty });
          return;
        }
      }
      // The start of a lap: which id the board is about to open, and what it believed about the
      // server at that moment. If a later `resolve` says that id was absent, these two entries
      // together say whether the board was wrong or the experience changed underneath it.
      recordLoadEvent('note', {
        page: 'board', what: 'open-task', taskId: task.id, readOnly,
        ...sampleIds([...knownServerTaskIdsRef.current]),
      });
      navigate(`/app/task/${task.id}?experience=${experience?.id ?? ''}`);
    },
    [navigate, experience, history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Experience-builder wiring: write a source task's whole flow via the flow-only per-task endpoint
  // (NOT persistTasks, which strips flow), so it can't clobber block content. Optimistic: apply the
  // flow to the source task locally (one undo entry), then reconcile only its contentRevision from
  // the server so a rapid follow-up on the same task sends the right expected revision. Other local
  // unsaved structural edits are preserved (we only ever touch the source task's flow). Callers must
  // build newFlow from a SINGLE read of the source task — never chain two of these on one task in a
  // tick (the second would read stale, pre-render local state and clobber the first).
  const persistFlow = useCallback(
    async (sourceTaskId: string, newFlow: TaskFlowConfig) => {
      if (readOnly || !experience) return;
      const src = history.tasks.find((tk) => tk.id === sourceTaskId);
      if (!src) return;
      // The depth cap, applied where the branch is actually CREATED rather than at each gesture
      // that leads here. This was the largest hole: drag-drop and keyboard-indent both checked, and
      // the flow-first path — which is how most branches get made — did not check at all. Soft by
      // decision, so this refuses to CREATE past the cap and never touches data already deeper.
      if (!fitsBranchDepth(branchDepth(history.tasks, sourceTaskId), [])) return;
      const optimistic = history.tasks.map((tk) => (tk.id === sourceTaskId ? { ...tk, flow: newFlow } : tk));
      history.push(optimistic);
      const snapshot = JSON.stringify(optimistic);
      try {
        const updated = await api.patch<Experience>(
          `/experiences/${experience.id}/tasks/${sourceTaskId}`,
          { flow: newFlow, expectedRevision: src.contentRevision },
        );
        const serverSrc = updated.tasks.find((tk) => tk.id === sourceTaskId);
        syncedFlowRef.current.set(sourceTaskId, JSON.stringify(newFlow));
        if (serverSrc && JSON.stringify(history.tasks) === snapshot) {
          history.replace(optimistic.map((tk) => (tk.id === sourceTaskId ? { ...tk, contentRevision: serverSrc.contentRevision } : tk)));
        }
      } catch {
        // 409 (edited elsewhere) or any failure → revert the optimistic flow change, warn.
        if (JSON.stringify(history.tasks) === snapshot) history.replace(history.tasks.map((tk) => (tk.id === sourceTaskId ? src : tk)));
        setSaveFailed(true);
        setToastMsg(t('experience.errors.couldNotSave'));
      }
    },
    [experience, history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Point one event at a target: a task id → goto; 'end' → terminate; 'stay' → disconnect.
  const handleWireEvent = useCallback(
    (sourceTaskId: string, eventId: string, target: string) => {
      const src = history.tasks.find((tk) => tk.id === sourceTaskId);
      if (!src) return;
      const action: FlowAction = target === 'end' ? { type: 'end' } : target === 'next' ? { type: 'next' } : target === 'stay' ? { type: 'stay' } : { type: 'goto', targetTaskId: target };
      void persistFlow(sourceTaskId, withEventAction(src.flow ?? DEFAULT_FLOW_CONFIG, eventId, action));
    },
    [history.tasks, persistFlow],
  );

  // Disconnect a wire: revert EVERY branch event on it to retry-in-place, folded into ONE flow so
  // the events don't overwrite each other (a shared-target wire de-dupes several events onto one).
  const handleDisconnectEvents = useCallback(
    (sourceTaskId: string, eventIds: string[]) => {
      const src = history.tasks.find((tk) => tk.id === sourceTaskId);
      if (!src || !eventIds.length) return;
      const newFlow = eventIds.reduce((flow, id) => withEventAction(flow, id, { type: 'stay' }), src.flow ?? DEFAULT_FLOW_CONFIG);
      void persistFlow(sourceTaskId, newFlow);
    },
    [history.tasks, persistFlow],
  );

  // Every pathway must carry a non-empty, UNIQUE name — Flow will reference pathways by name, so a
  // blank or duplicated name is a data hazard. This fills any unnamed pathway with the next free
  // "Pathway N" and canonicalises the (denormalised) name across a pathway's members. Idempotent:
  // already-named pathways are untouched. Run it wherever a pathway can be born or reshaped.
  const withPathwayNames = useCallback(
    (tasks: Task[]): Task[] => {
      const canonical = new Map<string, string>(); // pathwayId → chosen name
      const taken = new Set<string>();
      for (const tk of tasks) {
        const nm = (tk.pathwayName ?? '').trim();
        if (tk.pathwayId && nm && !canonical.has(tk.pathwayId)) { canonical.set(tk.pathwayId, nm); taken.add(nm.toLowerCase()); }
      }
      let n = 1;
      for (const tk of tasks) {
        if (!tk.pathwayId || canonical.has(tk.pathwayId)) continue;
        let name = t('experience.builder.defaultPathwayName', { n });
        while (taken.has(name.toLowerCase())) { n += 1; name = t('experience.builder.defaultPathwayName', { n }); }
        taken.add(name.toLowerCase()); canonical.set(tk.pathwayId, name); n += 1;
      }
      return tasks.map((tk) => (tk.pathwayId ? { ...tk, pathwayName: canonical.get(tk.pathwayId) } : tk));
    },
    [t],
  );

  // Wire an event to a task that doesn't exist yet: create a new CONTEXTUALIZED branch task off the
  // source (pathwayOf = source), wire the event to it, and open its editor. Sequenced off server
  // responses (not the optimistic history closure) so the goto target provably exists before the
  // flow write, and the source's real contentRevision is used. Navigating away means we don't need
  // to reconcile local history for the flow write — the board re-fetches fresh on return.
  const handleWireToNewBranch = useCallback(
    async (sourceTaskId: string, eventId: string) => {
      if (readOnly || !experience) return;
      const src = history.tasks.find((tk) => tk.id === sourceTaskId);
      if (!src) return;
      // The depth cap, applied where the branch is actually CREATED rather than at each gesture
      // that leads here. This was the largest hole: drag-drop and keyboard-indent both checked, and
      // the flow-first path — which is how most branches get made — did not check at all. Soft by
      // decision, so this refuses to CREATE past the cap and never touches data already deeper.
      if (!fitsBranchDepth(branchDepth(history.tasks, sourceTaskId), [])) return;
      const newTask: Task = {
        id: `tsk_${crypto.randomUUID()}`,
        title: t('experience.newTask'),
        level: src.level + 1,
        order: 0,
        version: 1,
        createdAt: new Date().toISOString(),
        ...newTaskScoring(experience.scoringDefault, true),
        blocks: [],
        pathwayOf: sourceTaskId,
        // Every new remediation branch is its own bounded pathway (pathway) from birth, so a
        // no-return end dead-ends here instead of leaking into a sibling remediation off the same host.
        pathwayId: `pw_${crypto.randomUUID()}`,
      };
      // 1) Create + persist the branch task (structural) so the goto target exists server-side.
      const created = await persistTasks(withPathwayNames([...history.tasks, newTask]));
      if (!created) return; // save failed — persistTasks already surfaced the toast
      // 2) Wire the source event to it (flow-only), using the server-confirmed source revision.
      const srcNow = created.tasks.find((tk) => tk.id === sourceTaskId);
      const newFlow = withEventAction(srcNow?.flow ?? src.flow ?? DEFAULT_FLOW_CONFIG, eventId, { type: 'goto', targetTaskId: newTask.id });
      try {
        await api.patch(`/experiences/${experience.id}/tasks/${sourceTaskId}`, { flow: newFlow, expectedRevision: srcNow?.contentRevision });
      } catch {
        setSaveFailed(true);
        setToastMsg(t('experience.errors.couldNotSave'));
        // The task was created; still open it so the teacher can wire it by hand if needed.
      }
      // 3) Create-and-contextualize: open the new task's editor.
      navigate(`/app/task/${newTask.id}?experience=${experience.id}`);
    },
    [experience, history.tasks, readOnly, persistTasks, navigate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Insert a task right after `afterTaskId` in its own lane (spine or branch), auto-rewiring the
  // through-line: prev.correct → new → prev's OLD correct target. For a loose `next` this is just an
  // array splice (deriveGraph resnaps prev→new→old-next). For a LOCKED correct (a return/skip/End)
  // the target migrates onto the new task and prev falls to `next` — so extending past a wired
  // return carries the return to the new last task. Unlike create-and-contextualize this does NOT
  // navigate — you're threading the spine, not authoring a branch.
  const handleInsertAfter = useCallback(
    async (afterTaskId: string) => {
      if (readOnly || !experience) return;
      const prev = history.tasks.find((tk) => tk.id === afterTaskId);
      if (!prev) return;
      const prevCorrect = prev.flow?.allCorrectAction;
      const migrate = prevCorrect != null && (prevCorrect.type === 'goto' || prevCorrect.type === 'end');
      const newTask: Task = {
        id: `tsk_${crypto.randomUUID()}`,
        title: t('experience.newTask'),
        level: prev.level + 1,
        order: 0,
        version: 1,
        createdAt: new Date().toISOString(),
        ...newTaskScoring(experience.scoringDefault, !!prev.pathwayOf),
        blocks: [],
        ...(prev.pathwayOf ? { pathwayOf: prev.pathwayOf } : {}),
        // Extending a pathway stays inside the same pathway (inherit name/collapsed too).
        ...(prev.pathwayId ? { pathwayId: prev.pathwayId, pathwayName: prev.pathwayName, pathwayCollapsed: prev.pathwayCollapsed } : {}),
        ...(migrate ? { flow: { ...DEFAULT_FLOW_CONFIG, allCorrectAction: prevCorrect } } : {}),
      };
      const idx = history.tasks.findIndex((tk) => tk.id === afterTaskId);
      const tasks = [...history.tasks.slice(0, idx + 1), newTask, ...history.tasks.slice(idx + 1)];
      history.push(tasks);
      setFocusTaskId(newTask.id); // move keyboard focus onto the inserted card
      const created = await persistTasks(tasks);
      if (!created || !migrate) return; // loose case: array order alone rewires prev→new→old-next
      // Locked correct migrated to the new task; drop prev to `next` so it flows into the new one.
      const prevNow = created.tasks.find((tk) => tk.id === afterTaskId);
      const newFlow = { ...(prevNow?.flow ?? DEFAULT_FLOW_CONFIG), allCorrectAction: { type: 'next' as const } };
      try {
        await api.patch(`/experiences/${experience.id}/tasks/${afterTaskId}`, { flow: newFlow, expectedRevision: prevNow?.contentRevision });
      } catch {
        setSaveFailed(true);
        setToastMsg(t('experience.errors.couldNotSave'));
      }
    },
    [experience, history.tasks, readOnly, persistTasks], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Duplicate a whole pathway as a SIBLING off the same host — copy + paste-as-branch, on the one
   * server path, not a second implementation.
   *
   * This was the third hand-rolled clone in the codebase, and it had the same bug family as the two
   * before it: every conditional rule was discarded, a wire out of the set became `stay` instead of
   * keeping its target, and an authored `null` loose thread became `stay` too. The other two were
   * retired by routing them through the paste endpoint; this one could not follow until the
   * endpoint knew how to place a pathway, because pasting after a task inherits that task's lane
   * and would have flattened the copy onto the spine.
   *
   * The copy's NAME is chosen here, not on the server: the server has no i18n and disambiguates
   * with a number, so the word "copy" has to come from a translated string.
   */
  const handleDuplicatePathway = useCallback(
    (pathwayId: string) => {
      if (readOnly) return;
      const members = history.tasks.filter((tk) => tk.pathwayId === pathwayId);
      const host = members[0]?.pathwayOf;
      // A pathway is defined by the task it hangs off; without one there is nothing to duplicate it
      // onto, and the endpoint would have nowhere to put it.
      if (!members.length || !host) return;
      const copyName = members[0]!.pathwayName?.trim()
        ? t('common.copyOf', { name: members[0]!.pathwayName!.trim() })
        : undefined;
      void pasteTasksInto(
        members.map((m) => ({ ...toClipboardTask(m), pathwayName: copyName })),
        { asBranchOfTaskId: host },
      );
    },
    [history.tasks, readOnly, pasteTasksInto, toClipboardTask, t],
  );

  // Cross-lane drag: make an existing spine task a branch of `parentId` (set pathwayOf). It leaves
  // the spine — deriveGraph restitches the neighbours by array order — and lands in the branch
  // column. Both are spine tasks, so no pathwayOf cycle is possible. Placement only; wire an event
  // to it afterward from the parent's port (an unwired branch is a valid, flagged state).
  const handleMakeBranchOf = useCallback(
    (taskId: string, parentId: string) => {
      if (readOnly || taskId === parentId) return;
      const tk = history.tasks.find((t) => t.id === taskId);
      if (!tk || tk.pathwayOf === parentId) return;
      // Guard against a pathwayOf cycle: the new parent must not be a descendant of the task moving.
      const childrenOf = (id: string) => history.tasks.filter((t) => t.pathwayOf === id).map((t) => t.id);
      const descendants = new Set<string>();
      const stack = childrenOf(taskId);
      while (stack.length) { const d = stack.pop()!; if (descendants.has(d)) continue; descendants.add(d); stack.push(...childrenOf(d)); }
      if (descendants.has(parentId)) return;
      // Depth, at the handler rather than only in the drag hit-test that usually precedes it — the
      // keyboard indent and any future caller reach this function without passing through that.
      // The whole SUBTREE moves, not just this task, so its own nesting counts against the cap;
      // `descendants` is already to hand from the cycle check above.
      const moving = history.tasks.filter((x) => x.id === taskId || descendants.has(x.id));
      if (!fitsBranchDepth(branchDepth(history.tasks, parentId), moving)) return;
      // Indent = a new remediation pathway off `parentId` → its own pathway (auto-named).
      history.push(withPathwayNames(history.tasks.map((t) => (t.id === taskId ? { ...t, pathwayOf: parentId, pathwayId: `pw_${crypto.randomUUID()}`, pathwayName: '', pathwayCollapsed: false } : t))));
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // New-builder scoring aspect: set one task's scoring (autosave persists via the dirty-tasks debounce).
  const handleSetTaskScoring = useCallback(
    (taskId: string, patch: { scoringMode: ScoringMode; points: number; penalty: number }) => {
      if (readOnly) return;
      history.push(history.tasks.map((tk) => (tk.id === taskId ? { ...tk, ...patch } : tk)));
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Experience-level scoring default. Routed through the undo stack (pushDefault) so it reverts with
  // Undo like the two bulk actions; the sync effect below mirrors it into experience state + persists.
  const handleSetScoringDefault = useCallback(
    (def: ScoringDefault) => {
      if (readOnly) return;
      history.pushDefault(def);
    },
    [readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Mirror the undo stack's scoring default into experience state and persist it. Fires on a user change
  // (pushDefault) AND on undo/redo (cursor move). Value-compared so it no-ops on load (reset seeds it
  // equal) and so different stack entries holding the same value don't trigger a redundant write.
  useEffect(() => {
    if (!experience || readOnly) return;
    const next = history.scoringDefault;
    if (JSON.stringify(next ?? null) === JSON.stringify(experience.scoringDefault ?? null)) return;
    setExperience((prev) => (prev ? { ...prev, scoringDefault: next } : prev));
    api.patch(`/experiences/${experience.id}`, { scoringDefault: next ?? null }).catch(() => {
      setSaveFailed(true);
      setToastMsg(t('experience.errors.couldNotSave'));
    });
  }, [history.scoringDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stamp the current default across the MAIN FLOW (spine) tasks; pathway tasks are left as-is (they
  // default to ungraded and a teacher may have deliberately graded one — don't clobber it).
  /**
   * Stamp the default onto the SELECTED tasks.
   *
   * Not armed, unlike Apply to Main Flow. That one reaches every spine task whether you can see it
   * or not, which is what earns it a confirm; this one reaches exactly what is lit on the board, and
   * picking them was the confirmation.
   */
  const handleApplyScoringToSelected = useCallback(() => {
    if (readOnly || !experience || !selectedIds.size) return;
    const def = experience.scoringDefault;
    history.push(history.tasks.map((tk) => (
      selectedIds.has(tk.id) ? { ...tk, ...newTaskScoring(def, false) } : tk
    )));
    setSelectedIds(new Set());
  }, [experience, history.tasks, readOnly, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyScoringToAll = useCallback(() => {
    if (readOnly || !experience) return;
    const def = experience.scoringDefault;
    history.push(history.tasks.map((tk) => (tk.pathwayOf ? tk : { ...tk, ...newTaskScoring(def, false) })));
  }, [experience, history.tasks, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Zero every PATHWAY task back to ungraded (spine untouched) — the one-click rebalance for the
  // imbalance the balance summary flags. Deliberately clobbers any pathway a teacher graded by hand
  // (that IS the reset), so it is a two-click armed action like Apply to Main Flow.
  const handleClearPathwayScores = useCallback(() => {
    if (readOnly || !experience) return;
    history.push(history.tasks.map((tk) => (tk.pathwayOf ? { ...tk, ...newTaskScoring(undefined, true) } : tk)));
  }, [experience, history.tasks, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTitleChange = useCallback(
    (title: string) => {
      if (readOnly) return;
      setExperience((prev) => (prev ? { ...prev, title } : prev));
    },
    [readOnly],
  );

  const handleReorder = useCallback(
    (fromId: string, toLevel: number) => {
      if (readOnly) return;
      const from = history.tasks.find((t) => t.id === fromId);
      if (!from || from.level === toLevel) return;
      const fromLevel = from.level;
      history.push(
        history.tasks.map((t) => {
          if (t.id === fromId) return { ...t, level: toLevel, order: 0 };
          if (toLevel > fromLevel && t.level > fromLevel && t.level <= toLevel) return { ...t, level: t.level - 1 };
          if (toLevel < fromLevel && t.level >= toLevel && t.level < fromLevel) return { ...t, level: t.level + 1 };
          return t;
        }),
      );
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Commit a full reordered/re-parented tasks array — the result of a drag drop (the board computes
  // the post-drop array itself so it matches the live preview exactly).
  const handleReorderTasks = useCallback(
    (next: Task[]) => { if (!readOnly) history.push(withPathwayNames(next)); },
    [readOnly, withPathwayNames], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Reorder a card UP/DOWN within its own lane (spine or a branch group), keyboard rail. Swaps its
  // array position with the adjacent same-lane sibling — deriveGraph orders each lane by array
  // position, so this reorders the lane without disturbing any other lane.
  const handleMoveWithinLane = useCallback(
    (taskId: string, dir: -1 | 1) => {
      if (readOnly) return;
      const laneKey = (t: Task) => t.pathwayOf ?? '__spine';
      const self = history.tasks.find((t) => t.id === taskId);
      if (!self) return;
      const key = laneKey(self);
      const lane = history.tasks.map((t, i) => ({ t, i })).filter((x) => laneKey(x.t) === key);
      const pos = lane.findIndex((x) => x.t.id === taskId);
      const target = lane[pos + dir];
      if (!target) return;
      const arr = [...history.tasks];
      const iA = lane[pos]!.i, iB = target.i;
      [arr[iA], arr[iB]] = [arr[iB]!, arr[iA]!];
      history.push(arr);
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Outdent (`<`): pop a branch card out to its PARENT's lane (one level shallower; at column 1 the
  // parent is the spine, so it promotes). Positions the card RIGHT AFTER its former parent in the
  // target lane — like a spine reorder displacing neighbours — instead of dumping it at the end.
  // Indent (`>`) reuses handleMakeBranchOf against the card above.
  const handleOutdent = useCallback(
    (taskId: string) => {
      if (readOnly) return;
      const arr = [...history.tasks];
      const ti = arr.findIndex((t) => t.id === taskId);
      const task = arr[ti];
      if (!task?.pathwayOf) return; // already on the spine
      const parentId = task.pathwayOf;
      const parent = arr.find((t) => t.id === parentId);
      // Leaving its lane exits its pathway (spine tasks are never pathwayd; a new lane is a new pathway).
      const moved: Task = { ...task, pathwayOf: parent?.pathwayOf, pathwayId: undefined, pathwayName: undefined, pathwayCollapsed: undefined };
      arr.splice(ti, 1);
      const pi = arr.findIndex((t) => t.id === parentId); // parent's index after removal
      arr.splice(pi < 0 ? arr.length : pi + 1, 0, moved); // slot in right after the former parent
      history.push(arr);
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Pathways (remediation pathways) ────────────────────────────────────────────────────────────
  // Name / collapse are denormalised onto every member task (read from any, written to all), so all
  // of this rides the existing structural task-persist path — no experience-level entity needed.
  const handleRenamePathway = useCallback(
    (pathwayId: string, rawName: string) => {
      if (readOnly) return;
      const desired = rawName.trim();
      // Names in use by OTHER pathways (case-insensitive) — the new name must not collide with them.
      const taken = new Set(
        history.tasks
          .filter((t2) => t2.pathwayId && t2.pathwayId !== pathwayId && (t2.pathwayName ?? '').trim())
          .map((t2) => t2.pathwayName!.trim().toLowerCase()),
      );
      let name: string;
      if (!desired) {
        // Cleared → fall back to the next free default (never leave a pathway nameless).
        let n = 1; name = t('experience.builder.defaultPathwayName', { n });
        while (taken.has(name.toLowerCase())) { n += 1; name = t('experience.builder.defaultPathwayName', { n }); }
      } else {
        // Requested name taken → append " 2", " 3", … until unique.
        name = desired; let k = 2;
        while (taken.has(name.toLowerCase())) { name = `${desired} ${k}`; k += 1; }
      }
      history.push(history.tasks.map((t2) => (t2.pathwayId === pathwayId ? { ...t2, pathwayName: name } : t2)));
    },
    [history.tasks, readOnly, t], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const handleTogglePathwayCollapsed = useCallback(
    (pathwayId: string) => {
      if (readOnly) return;
      const collapsed = !history.tasks.find((t) => t.pathwayId === pathwayId)?.pathwayCollapsed;
      history.push(history.tasks.map((t) => (t.pathwayId === pathwayId ? { ...t, pathwayCollapsed: collapsed } : t)));
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Collapse or expand EVERY pathway at once (the canvas minibar). No-op when already in that state so
  // it doesn't spam the undo stack.
  const handleSetAllPathwaysCollapsed = useCallback(
    (collapsed: boolean) => {
      if (readOnly) return;
      if (!history.tasks.some((t) => t.pathwayId && !!t.pathwayCollapsed !== collapsed)) return;
      history.push(history.tasks.map((t) => (t.pathwayId ? { ...t, pathwayCollapsed: collapsed } : t)));
    },
    [history.tasks, readOnly], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Save ────────────────────────────────────────────────────────────────

  /** The single place that talks to `PATCH /experiences/:id`. Takes an explicit `tasks` array
   * rather than reading `history.tasks` from closure — callers that just called `history.push`
   * in the same tick need to persist the array they JUST computed, since `history.tasks` won't
   * reflect it until the next render (React state updates aren't synchronous). The backend
   * reconciles this against what's stored rather than blind-replacing (see experiences.service.ts
   * `update()`'s own comment) — it's always safe to call with the full current structural view.
   *
   * Omits content (blocks/variables/flow) for any task the server already knows
   * about before sending — the backend ignores that content for an existing task regardless (see
   * the service's own reconcile comment), so sending it is pure waste. This isn't just tidiness:
   * round-tripping every existing task's full content on every structural save (a reorder, a
   * points edit, deleting an unrelated task) made a real, content-heavy experience's payload
   * exceed the server's request-body size limit outright — confirmed live against a real 4-task
   * lesson (141KB, rejected with "request entity too large"). Only genuinely new tasks
   * (create/duplicate) need their content included, since that's the one case where it's actually
   * being persisted for the first time. */
  function persistTasks(tasks: Task[], title: string = experience?.title ?? ''): Promise<Experience | null> {
    const p = runPersistTasks(tasks, title);
    savingPromiseRef.current = p;
    // Clear the ref once settled so handleEditTask doesn't await an already-resolved stale promise.
    void p.finally(() => { if (savingPromiseRef.current === p) savingPromiseRef.current = null; });
    return p;
  }

  async function runPersistTasks(tasks: Task[], title: string): Promise<Experience | null> {
    if (readOnly || !experience) return null;
    const callSiteSnapshot = JSON.stringify(history.tasks); // see the guard below
    setSaving(true);
    setSaveFailed(false);
    try {
      const payloadTasks = tasks.map((task) => {
        if (!knownServerTaskIdsRef.current.has(task.id)) return task;
        const structural: Partial<Task> = { ...task };
        delete structural.blocks;
        delete structural.variables;
        delete structural.flow;
        return structural;
      });
      const updated = await api.patch<Experience>(`/experiences/${experience.id}`, { title, tasks: payloadTasks });
      setExperience(updated);
      // Only reconcile the undo stack's current top entry with the server's confirmed copy if
      // nothing else has changed locally since this save started (e.g. the user hit Undo while
      // this request was in flight) — otherwise this would silently overwrite whatever they've
      // since moved on to with stale-relative-to-now server data. Crucially, KEEP the local flow:
      // this structural PATCH strips flow, so `updated.tasks` carries the server's flow, which may be
      // a wire the user just undid. Flow reaches the server only via persistFlowDiffs / persistFlow.
      // `reconciled` (server structural + local flow) is used for history AND the saved snapshot AND
      // syncedFlow — they MUST agree, or `isDirty` compares local flow to the server's normalised
      // flow (subdoc ids / key order / defaults differ) and never settles → a perpetual save loop.
      const localById = new Map(tasks.map((tk) => [tk.id, tk]));
      const reconciled = updated.tasks.map((st) => {
        const local = localById.get(st.id);
        return local ? { ...st, flow: local.flow } : st;
      });
      if (JSON.stringify(history.tasks) === callSiteSnapshot) history.replace(reconciled);
      savedSnapshotRef.current = JSON.stringify({ title: updated.title, tasks: reconciled });
      knownServerTaskIdsRef.current = new Set(updated.tasks.map((t) => t.id));
      syncedFlowRef.current = new Map(reconciled.map((st) => [st.id, JSON.stringify(st.flow ?? DEFAULT_FLOW_CONFIG)]));
      return updated;
    } catch {
      setSaveFailed(true);
      setToastMsg(t('experience.errors.couldNotSave'));
      return null;
    } finally {
      setSaving(false);
    }
  }

  // Push any task whose LOCAL flow diverged from what the server holds (syncedFlowRef) back through
  // the flow-only endpoint. This is what makes an Undo/Redo of a wire durable: undo reverts the
  // source task's flow locally; this re-persists that reverted flow so it sticks, instead of the
  // structural reconcile pulling the stale server wire back in.
  async function persistFlowDiffs(tasks: Task[]): Promise<void> {
    if (readOnly || !experience) return;
    for (const task of tasks) {
      if (!knownServerTaskIdsRef.current.has(task.id)) continue; // brand-new tasks carry flow via persistTasks
      const localFlow = JSON.stringify(task.flow ?? DEFAULT_FLOW_CONFIG);
      if (syncedFlowRef.current.get(task.id) === localFlow) continue;
      try {
        // No expectedRevision: Undo reverts contentRevision too, so a revision check would 409 against
        // the server's post-wire revision. The builder is authoritative for its own flow.
        await api.patch<Experience>(`/experiences/${experience.id}/tasks/${task.id}`, { flow: task.flow ?? DEFAULT_FLOW_CONFIG });
        syncedFlowRef.current.set(task.id, localFlow);
      } catch {
        setSaveFailed(true);
        setToastMsg(t('experience.errors.couldNotSave'));
      }
    }
  }

  /**
   * Which half goes first depends on whether any task is NEW to the server.
   *
   * Flow-first is right for the ordinary case: undoing a wire reverts the source task's flow, and
   * pushing that before the structural reconcile stops the reconcile pulling the stale server wire
   * back in.
   *
   * It is wrong the moment an undo RESTORES deleted tasks. The snapshot brings back both the tasks
   * and the wires that pointed at them, and a flow PATCH naming a task the server has not been told
   * about yet is rejected — `Flow action targets a task that does not exist`, surfaced as a save
   * failure after an undo that had in fact worked locally. Structure first in that case, so the
   * targets exist before anything points at them.
   */
  async function handleSave(): Promise<boolean> {
    const restoring = history.tasks.some((tk) => !knownServerTaskIdsRef.current.has(tk.id));
    if (restoring) {
      const saved = await persistTasks(history.tasks);
      if (saved) await persistFlowDiffs(history.tasks);
      return saved !== null;
    }
    await persistFlowDiffs(history.tasks);
    return (await persistTasks(history.tasks)) !== null;
  }

  const isDirty = experience
    ? JSON.stringify({ title: experience.title, tasks: history.tasks }) !== savedSnapshotRef.current
    : false;

  // Auto-save: any structural change (reorder, points edit, delete) reaches the server shortly
  // after, same debounced-on-dirty pattern as the task editor's own autosave — Undo/Redo stays
  // fully correct because undoing just becomes a local-state change that itself gets persisted
  // moments later, exactly like any other edit. Create/duplicate deliberately do NOT wait for
  // this — see their own persistTasks calls — since a teacher could plausibly click straight into
  // a just-created task's editor before this timer would fire.
  //
  // Depends on the actual content (`history.tasks`/title), not the derived `isDirty` boolean —
  // `isDirty` only FLIPS once (false→true) and then stays true across every subsequent edit until
  // a save succeeds, so an effect keyed on it schedules exactly one timer at the first edit and
  // never resets it; a second edit landing inside that same ~1s window would still fire the
  // ORIGINAL (now-stale) closure instead of debouncing from the latest change. Low-stakes here
  // (structural edits are infrequent/deliberate), but the same bug would be a real correctness
  // problem for the task editor's continuous-typing autosave — fixed in both places together.
  useEffect(() => {
    // readOnly is a belt-and-suspenders check for the moment take-over lands mid-edit — every
    // mutating handler above already refuses to run while read-only (persistTasks included), so
    // isDirty shouldn't normally become true in that state.
    if (!isDirty || readOnly) return;
    const timer = setTimeout(() => { void handleSave(); }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.tasks, experience?.title, readOnly]);

  /**
   * Every exit from the board, guarded — the logo and the collection crumb alike.
   *
   * Was `handleBack`, which only ever went to `/app`, because the back chevron was the only
   * guarded way out and the logo beside it went there unguarded. Generalising it is what let the
   * chevron go.
   */
  function guardedExit(run: () => void) {
    if (isDirty) { pendingExitRef.current = run; setShowUnsavedModal(true); }
    else run();
  }

  function handleNavigate(to: string) { guardedExit(() => navigate(to)); }

  /** Do whatever the modal was deciding about; fall back to home. */
  function runPendingExit() {
    const run = pendingExitRef.current ?? (() => navigate('/app'));
    pendingExitRef.current = null;
    run();
  }

  function handleLeaveWithoutSaving() {
    setShowUnsavedModal(false);
    runPendingExit();
  }

  async function handleSaveAndLeave() {
    const ok = await handleSave();
    if (ok) {
      setShowUnsavedModal(false);
      runPendingExit();
    }
  }

  const handleSizeChange = useCallback((cw: number, ch: number, tw: number, th: number) => {
    setCanvasContainerSize({ w: cw, h: ch });
    setCanvasContentSize({ w: tw, h: th });
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-ws-bg'>
        <p className='text-sm text-ws-muted'>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !experience) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center gap-4 bg-ws-bg'>
        <p className='text-sm text-red-500'>{error || t('experience.errors.somethingWrong')}</p>
        <button onClick={() => navigate('/app')} className='text-sm text-[#03258c] underline'>
          {t('experience.goHome')}
        </button>
      </div>
    );
  }

  return (
    <div className='flex h-screen flex-col overflow-hidden bg-ws-bg'>
      <ExperienceNavbar
        title={experience.title}
        collectionId={experience.collectionId}
        onNavigate={handleNavigate}
        onGuard={guardedExit}
      />

      {/* Directly under the breadcrumbs lane, which is the one row that stays live: navigating away
          has to remain possible, and it is the only way out once everything below is inert. */}
      {readOnly && <TakeOverBanner onResume={resume} />}

      {/* Everything below the banner is read-only, and says so. The title bar already disabled its
          own editing controls, which left it in a limbo — above the dimmed board, below the banner,
          and still looking live. `inert` + `aria-hidden` matches the treatment the board region has
          had all along. */}
      <div
        className='relative flex shrink-0 flex-col'
        {...(readOnly ? { inert: '' } : {})}
        aria-hidden={readOnly}
      >
        {readOnly && (
          <div aria-hidden='true' className='absolute inset-0 z-40 cursor-not-allowed bg-ws-surface/40' />
        )}
      <ExperienceTitleBar
        title={experience.title}
        saving={saving}
        saveFailed={saveFailed}
        isDirty={isDirty}
        canUndo={history.canUndo && !readOnly}
        canRedo={history.canRedo && !readOnly}
        onTitleSave={handleTitleChange}
        onSave={() => { void handleSave(); }}
        onUndo={() => { if (!readOnly) history.undo(); }}
        onRedo={() => { if (!readOnly) history.redo(); }}
        showTools={useNewBuilder && history.tasks.length > 0 && !narrowBars}
        selectActive={selectMode}
        onToggleSelect={() => (selectMode ? exitSelect() : setSelectMode(true))}
        onShowErrors={() => { setErrorsAspect((v) => !v); setScoringAspect(false); }}
        errorCount={problems.errors}
        errorsActive={errorsAspect}
        onShowScoring={() => { setScoringAspect((v) => !v); setErrorsAspect(false); }}
        scoringActive={scoringAspect}
        onPreview={startPreviewRun}
        readOnly={readOnly}
      />

      {/* Second toolbar — phone-portrait / very narrow only, where bar 1 has no room beside the title.
          Holds the builder tools (left) + the view controls the floating minibar carries elsewhere (right). */}
      {useNewBuilder && history.tasks.length > 0 && narrowBars && (
        <div className='flex shrink-0 items-center gap-1 border-b border-ws-border bg-ws-surface px-3 py-1.5'>
          {/*
            WIRED, and wired the same way bar one is.
            This bar is phone-portrait's only route to these tools, and three of its controls were
            markup without handlers: Errors and Preview did nothing at all, and Scoring toggled
            itself without standing Errors down. The symptom was exact — "problems cannot be turned
            on in phone portrait, but stays if you turn it on in landscape and switch" — because
            `sessionStorage` carried the aspect across the orientation change while portrait had no
            way to set it. Same fault as the Inspect toggle that reached one phone bar of three.

            The two aspects are MUTUALLY EXCLUSIVE, so each turns the other off, exactly as
            `onShowErrors` / `onShowScoring` do above. The count rides in the accessible name for
            the reason bar one gives: a listener gets "Errors, 7" rather than a bare "Errors"
            beside a badge they cannot see.
          */}
          <button type='button'
            onClick={() => { setErrorsAspect((v) => !v); setScoringAspect(false); }}
            aria-pressed={errorsAspect}
            aria-label={problems.errors > 0
              ? t('experience.tools.errorsWithCount', { count: problems.errors })
              : t('experience.tools.errors')}
            title={t('experience.tools.errors')}
            className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${errorsAspect ? 'bg-ws-error-bg text-ws-error-fg' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}>
            <TriangleAlert aria-hidden='true' className='h-4 w-4' />
          </button>
          <button type='button' onClick={() => { setScoringAspect((v) => !v); setErrorsAspect(false); }} aria-pressed={scoringAspect}
            aria-label={t('experience.tools.scoring')} title={t('experience.tools.scoring')}
            className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${scoringAspect ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}>
            <Trophy aria-hidden='true' className='h-4 w-4' />
          </button>
          {/* Grouped with Scoring: both are TOGGLES. Preview and Errors are actions, and sit apart. */}
          <button type='button' onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} aria-pressed={selectMode}
            aria-label={t('experience.select.title')} title={t('experience.select.title')}
            className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${selectMode ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}>
            <CheckSquare aria-hidden='true' className='h-4 w-4' />
          </button>
          <Button variant='primary' onClick={startPreviewRun}
            aria-label={t('experience.tools.preview')} title={t('experience.tools.preview')}
            icon={<Play aria-hidden='true' fill='currentColor' />}>
            {t('experience.tools.preview')}
          </Button>
          <div className='ml-auto flex items-center gap-1'>
            <button type='button' onClick={() => handleSetAllPathwaysCollapsed(true)} disabled={readOnly || !history.tasks.some((tk) => tk.pathwayId)}
              aria-label={t('experience.builder.collapseAll')} title={t('experience.builder.collapseAll')}
              className='flex h-9 w-9 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover disabled:pointer-events-none disabled:opacity-30'>
              <ChevronsDownUp aria-hidden='true' className='h-4 w-4' />
            </button>
            <button type='button' onClick={() => handleSetAllPathwaysCollapsed(false)} disabled={readOnly || !history.tasks.some((tk) => tk.pathwayId)}
              aria-label={t('experience.builder.expandAll')} title={t('experience.builder.expandAll')}
              className='flex h-9 w-9 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover disabled:pointer-events-none disabled:opacity-30'>
              <ChevronsUpDown aria-hidden='true' className='h-4 w-4' />
            </button>
            <button type='button' onClick={() => setShowMiniMap(true)}
              aria-label={t('experience.map.title')} title={t('experience.map.title')}
              className='flex h-9 w-9 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover'>
              <MapIcon aria-hidden='true' className='h-4 w-4' />
            </button>
          </div>
        </div>
      )}
      </div>

      {/*
        * The selection bar, in the same slot and shape as the wire-arming banner — they are
        * mutually exclusive states, so one transient-bar position serves both.
        *
        * Shown from the moment the mode opens, not from the first selection. The scoring aspect
        * announces itself by changing every card; select mode with nothing picked looks almost
        * like the ordinary board, and a teacher who forgot they were in it would click a card,
        * get nothing, and conclude the board was broken.
        */}
      {selectMode && (() => { const pillSize = coarsePointer ? 'lg' as const : 'md' as const; return (
        <div className='fixed inset-x-0 bottom-4 z-50 flex justify-center px-4'>
          <div className='flex max-w-full items-center gap-2 rounded-full border border-ws-primary/40 bg-ws-surface px-3 py-2 shadow-xl'>
            <span className='shrink-0 px-1 text-sm text-ws-text'>
              {selectedIds.size
                ? <span className='font-semibold'>{t('experience.select.count', { count: selectedIds.size })}</span>
                : <span className='text-ws-muted'>{t('experience.select.hint')}</span>}
            </span>
            {/* The app's own icon vocabulary, and its own controls: Copy/Duplicate/Delete are the
                same three verbs and the same three glyphs the card menu uses, and Delete is the
                standard two-click armed IconButton rather than a second confirm invented here. */}
            <IconButton size={pillSize} variant='primary' disabled={!selectedIds.size} onClick={handleCopySelection}
              aria-label={t('experience.paste.copyTask')} title={t('experience.paste.copyTask')}>
              <Copy className='h-4 w-4' />
            </IconButton>
            <IconButton size={pillSize} variant='primary' disabled={!selectedIds.size} onClick={handleDuplicateSelection}
              aria-label={t('common.duplicate')} title={t('common.duplicate')}>
              <CopyPlus className='h-4 w-4' />
            </IconButton>
            <IconButton
              size={pillSize}
              variant='destructive'
              disabled={!selectedIds.size || orphaningHosts.size > 0}
              isArmed={deleteArmed}
              onBlur={() => setDeleteArmed(false)}
              onPointerLeave={(e) => { if (deleteArmed && e.pointerType !== 'touch') setDeleteArmed(false); }}
              onClick={() => { if (!deleteArmed) { setDeleteArmed(true); return; } handleDeleteSelection(); setDeleteArmed(false); }}
              aria-label={orphaningHosts.size ? t('experience.select.deleteBlocked') : deleteArmed ? t('common.deleteConfirm') : t('common.delete')}
              title={orphaningHosts.size ? t('experience.select.deleteBlocked') : deleteArmed ? t('common.deleteConfirm') : t('common.delete')}>
              <Trash2 className='h-4 w-4' />
            </IconButton>
            <div aria-hidden='true' className='mx-0.5 h-5 w-px bg-ws-border' />
            <IconButton size={pillSize} onClick={exitSelect} aria-label={t('common.done')} title={t('common.done')}>
              <X className='h-4 w-4' />
            </IconButton>
          </div>
          <span className='sr-only' role='status' aria-live='polite'>
            {deleteArmed ? t('common.deleteConfirm') : t('experience.select.count', { count: selectedIds.size })}
          </span>
        </div>
      ); })()}

      {/* Board region. In the scoring aspect a left inspector (ScoringPanel) FLOATS over the board on wide
          screens (absolute — it must not reflow the board, or cards slide under the minimap); on
          tablet-portrait/phone it becomes a modal (rendered below). The board keeps full width either way. */}
      <div className='relative flex flex-1 overflow-hidden'>
        {/* `w-80` where the viewport is short: the panel FLOATS, so its width costs the board
            nothing in layout, but on an SE-class landscape phone 384px of overlay leaves very
            little of the board visible behind it. 320px is the figure Inspect's own dock uses. */}
        {errorsAspect && useNewBuilder && history.tasks.length > 0 && !scoringPanelAsSheet && (
          <aside className={`absolute inset-y-0 left-0 z-20 flex flex-col overflow-hidden border-r border-ws-border bg-ws-surface shadow-xl ${shortLandscape ? 'w-80' : 'w-96'}`}>
            <ProblemsPanel
              groups={problemGroups}
              storageKey={id}
              errors={problems.errors}
              warnings={problems.warnings}
              hoveredId={hoveredProblem?.id}
              onHover={setHoveredProblem}
              onGoTo={goToProblem}
              onExpandPathway={handleTogglePathwayCollapsed}
            />
          </aside>
        )}
        {scoringAspect && useNewBuilder && history.tasks.length > 0 && !scoringPanelAsSheet && (
          <aside className={`absolute inset-y-0 left-0 z-20 flex flex-col overflow-y-auto border-r border-ws-border bg-ws-surface px-4 py-4 shadow-xl ${shortLandscape ? 'w-80' : 'w-96'}`}>
            <ScoringPanel
              tasks={history.tasks}
              scoringDefault={experience.scoringDefault}
              readOnly={readOnly}
              onSetDefault={handleSetScoringDefault}
              applyArmed={scoringApplyArmed}
              onApply={() => { if (!scoringApplyArmed) { setScoringApplyArmed(true); return; } handleApplyScoringToAll(); setScoringApplyArmed(false); }}
              onApplyBlur={() => setScoringApplyArmed(false)}
              pulse={scoringPulse}
              selectedCount={selectedIds.size}
              onApplyToSelected={handleApplyScoringToSelected}
              clearArmed={scoringClearArmed}
              onClear={() => { if (!scoringClearArmed) { setScoringClearArmed(true); return; } handleClearPathwayScores(); setScoringClearArmed(false); }}
              onClearBlur={() => setScoringClearArmed(false)}
            />
          </aside>
        )}
      <div
        className='relative flex-1 overflow-hidden'
        {...(readOnly ? { inert: '' } : {})}
        aria-hidden={readOnly}
      >
        {/* Deliberately simple v1 read-only enforcement: every mutating handler above already
         * refuses to run while read-only regardless, so this is purely the visual/interaction
         * affordance layer, not the actual enforcement:
         * - the dimmed overlay is the pointer/visual signal for sighted mouse users
         * - `inert` (+ aria-hidden, matching this codebase's established
         *   block-editor-drawer.tsx/rich-text-editor.tsx pattern) removes the whole subtree from
         *   tab order AND the accessibility tree, so keyboard/screen-reader users can't land on a
         *   task card's Edit/Duplicate/Delete and silently no-op it with no explanation. */}
        {readOnly && (
          <div
            aria-hidden='true'
            className='absolute inset-0 z-40 cursor-not-allowed bg-ws-surface/40'
          />
        )}
        {/* Canvas minibar (upper-right): collapse-all / expand-all pathways + open the overview map.
            The collapse controls act on every pathway at once; disabled when there are no pathways.
            Dropped on narrow (phone-portrait) layouts — those controls live in the second bar instead. */}
        {useNewBuilder && history.tasks.length > 0 && !narrowBars && (
          <div className={`absolute right-4 top-4 z-30 flex gap-0.5 rounded-lg border border-ws-border bg-ws-surface/95 p-1 shadow-md backdrop-blur-sm ${minibarVertical ? 'flex-col' : 'items-center'}`}>
            <button
              type='button'
              onClick={() => handleSetAllPathwaysCollapsed(true)}
              disabled={readOnly || !history.tasks.some((tk) => tk.pathwayId)}
              className='flex h-8 w-8 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover disabled:pointer-events-none disabled:opacity-30'
              aria-label={t('experience.builder.collapseAll')}
              title={t('experience.builder.collapseAll')}
            >
              <ChevronsDownUp aria-hidden='true' className='h-4 w-4' />
            </button>
            <button
              type='button'
              onClick={() => handleSetAllPathwaysCollapsed(false)}
              disabled={readOnly || !history.tasks.some((tk) => tk.pathwayId)}
              className='flex h-8 w-8 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover disabled:pointer-events-none disabled:opacity-30'
              aria-label={t('experience.builder.expandAll')}
              title={t('experience.builder.expandAll')}
            >
              <ChevronsUpDown aria-hidden='true' className='h-4 w-4' />
            </button>
            <div className={minibarVertical ? 'my-0.5 h-px w-full bg-ws-border' : 'mx-0.5 h-5 w-px bg-ws-border'} />
            <button
              type='button'
              onClick={() => setShowMiniMap(true)}
              className='flex h-8 w-8 items-center justify-center rounded-md text-ws-text transition-colors hover:bg-ws-hover'
              aria-label={t('experience.map.title')}
              title={t('experience.map.title')}
            >
              <MapIcon aria-hidden='true' className='h-4 w-4' />
            </button>
          </div>
        )}
        {useNewBuilder ? (
          <SpineBuilder
            tasks={history.tasks}
            readOnly={readOnly}
            scrollRef={boardScrollRef}
            focusTaskId={focusTaskId}
            hoverTaskId={mapHoverTaskId}
            pulseTaskIds={pulseTaskIds}
            onFocusHandled={() => { setFocusTaskId(null); setPulseTaskIds(null); }}
            onEditTask={handleEditTask}
            onCreateTask={handleCreateTask}
            onDeleteTask={handleDeleteTaskDirect}
            onDuplicateTask={handleDuplicateTask}
            onReorder={handleReorderTasks}
            onMoveWithinLane={handleMoveWithinLane}
            onOutdent={handleOutdent}
            onRenamePathway={handleRenamePathway}
            onTogglePathwayCollapsed={handleTogglePathwayCollapsed}
            onCopyPathway={handleCopyPathway}
            onDuplicatePathway={handleDuplicatePathway}
            onDeletePathway={handleDeletePathway}
            onWireEvent={handleWireEvent}
            onDisconnectEvents={handleDisconnectEvents}
            onWireToNewBranch={handleWireToNewBranch}
            onInsertAfter={handleInsertAfter}
            onCopyTask={handleCopyTask}
            onPasteAfter={clipboardTasks ? handlePasteTasks : undefined}
            onPastePathwayOnto={clipboardTasks ? handlePastePathwayOnto : undefined}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onShiftSelect={(taskId) => { setSelectMode(true); setSelectedIds((prev) => new Set(prev).add(taskId)); }}
            selectedPathwayIds={selectedPathwayIds}
            onTogglePathwaySelected={togglePathwaySelected}
            pathwayClipboardDepth={clipboardDepth}
            pasteLabel={clipboardTasks?.[0]?.title}
            onMakeBranchOf={handleMakeBranchOf}
            scoringActive={scoringAspect}
            errorsActive={errorsAspect}
            problemsByTask={problemsByTask}
            highlighted={hoveredProblem
              ? { taskId: hoveredProblem.taskId, blockId: hoveredProblem.blockId }
              : undefined}
            onOpenBlock={(taskId, blockId) => goToTask(taskId, { blockId })}
            onSetTaskScoring={handleSetTaskScoring}
          />
        ) : (
          <WorkspaceCanvas
            zoom={canvas.zoom}
            panX={canvas.panX}
            panY={canvas.panY}
            locked={history.tasks.length === 0}
            onZoomAt={canvas.zoomAt}
            onSetZoom={canvas.setZoom}
            onSetPan={canvas.setPan}
            onFitContent={canvas.fitToContent}
            onSizeChange={handleSizeChange}
          >
            <WorkspaceContent
              tasks={history.tasks}
              isReorderMode={isReorderMode}
              onCreate={handleCreateTask}
              onEditTask={handleEditTask}
              onDeleteTask={handleDeleteTask}
              onDuplicateTask={handleDuplicateTask}
              onInsertAt={handleInsertAt}
              onReorder={handleReorder}
            />
          </WorkspaceCanvas>
        )}

        {showMiniMap && (
          useNewBuilder ? (
            <BuilderMapModal
              tasks={history.tasks}
              boardScrollRef={boardScrollRef}
              onHoverTask={setMapHoverTaskId}
              onClose={() => { setShowMiniMap(false); setMapHoverTaskId(null); }}
              onJumpToTask={(taskId) => {
                // The map shows every block, including members of a COLLAPSED pathway. Jumping to one
                // must open its pathway first so the card you picked is actually visible on the board.
                const task = history.tasks.find((tk) => tk.id === taskId);
                if (task?.pathwayId && task.pathwayCollapsed) handleTogglePathwayCollapsed(task.pathwayId);
                // Jumping from the map is the "look here" case by definition — you asked to be taken
                // to this card, so it should say which one it is when you arrive.
                focusAndPulse(taskId);
              }}
            />
          ) : (
            <MiniMapModal
              tasks={history.tasks}
              onClose={() => setShowMiniMap(false)}
              zoom={canvas.zoom}
              panX={canvas.panX}
              panY={canvas.panY}
              containerW={canvasContainerSize.w}
              containerH={canvasContainerSize.h}
              workspaceContentW={canvasContentSize.w}
              workspaceContentH={canvasContentSize.h}
              onPanChange={canvas.setPan}
              onZoomAt={canvas.zoomAt}
            />
          )
        )}
      </div>
      {/* Narrow: the problems panel as a bottom SHEET, the same split Scoring uses — a docked
          `aside` where there is room beside the board, a sheet where there is not. Fixed height
          rather than Scoring's peek-and-expand: this panel is a LIST, so what it needs is a
          scrollport, and a headline bar would only repeat the count already on the tool. */}
      {errorsAspect && useNewBuilder && history.tasks.length > 0 && scoringPanelAsSheet && (
        <div className='fixed inset-x-0 bottom-0 z-30 flex h-[52vh] flex-col rounded-t-2xl border-t border-ws-border bg-ws-surface shadow-[0_-8px_24px_rgba(15,23,42,0.10)]'>
          <ProblemsPanel
            groups={problemGroups}
            storageKey={id}
            errors={problems.errors}
            warnings={problems.warnings}
            hoveredId={hoveredProblem?.id}
            onHover={setHoveredProblem}
            onGoTo={goToProblem}
            onExpandPathway={handleTogglePathwayCollapsed}
          />
        </div>
      )}
      {/* Narrow (tablet/phone): the scoring panel as a bottom SHEET. It peeks — a slim headline bar (Main
          Flow total + imbalance flag) that leaves the board fully editable — and expands on tap into the
          full panel. NOT a modal (no focus trap): a light scrim appears only while expanded and taps back
          to the peek. Computed breakdown feeds the peek headline. */}
      {scoringAspect && useNewBuilder && history.tasks.length > 0 && scoringPanelAsSheet && (() => {
        const bd = scoringBreakdown(history.tasks);
        return (
          <>
            <button
              type='button'
              aria-hidden='true'
              tabIndex={-1}
              onClick={() => setScoringSheetExpanded(false)}
              className={`absolute inset-0 z-20 bg-black/20 transition-opacity ${scoringSheetExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            />
            <div
              className='absolute inset-x-0 bottom-0 z-30 flex flex-col rounded-t-2xl border-t border-ws-border bg-ws-surface shadow-[0_-12px_32px_-16px_rgba(0,0,0,0.4)] transition-transform duration-300'
              style={{ maxHeight: '78%', transform: scoringSheetExpanded ? 'translateY(0)' : 'translateY(calc(100% - 52px))' }}
            >
              <button
                type='button'
                aria-expanded={scoringSheetExpanded}
                aria-label={t('experience.tools.scoring')}
                onPointerDown={(e) => { sheetDragStartY.current = e.clientY; sheetDidDrag.current = false; }}
                onPointerMove={(e) => { if (sheetDragStartY.current !== null && Math.abs(e.clientY - sheetDragStartY.current) > 8) sheetDidDrag.current = true; }}
                onPointerUp={(e) => {
                  if (sheetDragStartY.current === null) return;
                  const dy = e.clientY - sheetDragStartY.current;
                  sheetDragStartY.current = null;
                  if (sheetDidDrag.current) setScoringSheetExpanded(dy < 0); // dragged up = expand, down = collapse
                }}
                onPointerCancel={() => { sheetDragStartY.current = null; }}
                onClick={() => { if (sheetDidDrag.current) { sheetDidDrag.current = false; return; } setScoringSheetExpanded((v) => !v); }}
                className='relative flex h-[52px] shrink-0 touch-none items-center gap-2 px-4 text-left'
              >
                <span aria-hidden='true' className='absolute left-1/2 top-2 h-1 w-9 -translate-x-1/2 rounded-full bg-ws-border' />
                <span className='text-sm font-semibold text-ws-text'>{t('experience.builder.mainFlow')}</span>
                <span className='font-semibold tabular-nums text-ws-text'>{bd.mainFlowTotal} {t('experience.scoring.pts')}</span>
                {(bd.anyGraded || bd.anyPrematureEnd) && <TriangleAlert aria-hidden='true' className='h-4 w-4 text-ws-warning-fg' />}
                <ChevronUp aria-hidden='true' className={`ml-auto h-5 w-5 text-ws-muted transition-transform ${scoringSheetExpanded ? 'rotate-180' : ''}`} />
              </button>
              <div className='min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-1'>
                <ScoringPanel
                  tasks={history.tasks}
                  scoringDefault={experience.scoringDefault}
                  readOnly={readOnly}
                  onSetDefault={handleSetScoringDefault}
                  applyArmed={scoringApplyArmed}
                  onApply={() => { if (!scoringApplyArmed) { setScoringApplyArmed(true); return; } handleApplyScoringToAll(); setScoringApplyArmed(false); }}
                  onApplyBlur={() => setScoringApplyArmed(false)}
                  pulse={scoringPulse}
                  selectedCount={selectedIds.size}
                  onApplyToSelected={handleApplyScoringToSelected}
                  clearArmed={scoringClearArmed}
                  onClear={() => { if (!scoringClearArmed) { setScoringClearArmed(true); return; } handleClearPathwayScores(); setScoringClearArmed(false); }}
                  onClearBlur={() => setScoringClearArmed(false)}
                />
              </div>
            </div>
          </>
        );
      })()}
      </div>

      {pendingPull && (
        <PullThroughDialog
          rows={pendingPull.rows}
          // Dismissing abandons the paste: the teacher was asked a question about it and has
          // not answered, so committing either version of it would be deciding for them.
          onCancel={() => setPendingPull(null)}
          onSkip={() => {
            const { tasks, where } = pendingPull;
            setPendingPull(null);
            void pasteTasksInto(tasks, where);
          }}
          onConfirm={(ids) => {
            const { tasks, where, sourceTasks, rows, sourceAliases, sourceVariables } = pendingPull;
            setPendingPull(null);
            const pulled = pullPasteOrder(rows, ids)
              .map(id => sourceTasks.find(tk => tk.id === id))
              .filter((tk): tk is Task => !!tk)
              .map(tk => ({ ...snapshotTask(tk, sourceVariables), pathwayOf: null }));
            void pasteTasksInto(
              tasks,
              where,
              /*
               * DEEPEST FIRST, which is the REVERSE of how they are listed.
               *
               * The list reads outward from the paste — "this needs Setup, and Setup needs
               * Field work" — but the sequence has to run the other way, because a reference
               * resolves only if the answer is reached BEFORE the task citing it. Pasted
               * shallowest first, Setup would land ahead of the Field work it depends on and
               * arrive broken, having been brought along specifically so it would not.
               *
               * `pathwayOf: null` makes each a ROOT: it lands in the anchor's lane, directly
               * ahead of the paste, whatever lane that is.
               *
               * Snapshotted from the SOURCE experience, not through `toClipboardTask`, which
               * would hand them this experience's variables.
               */
              pulled,
              // Their own chips cite answers too, and the clipboard's census never saw them.
              sourceAliases,
            );
          }}
        />
      )}

      {deleteTask && (
        <DeleteConfirmModal
          taskTitle={deleteTask.title}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTask(null)}
        />
      )}

      {showUnsavedModal && (
        <UnsavedChangesModal
          onCancel={() => setShowUnsavedModal(false)}
          onLeave={handleLeaveWithoutSaving}
          onSaveAndLeave={handleSaveAndLeave}
        />
      )}

      {toastMsg && <Toast message={toastMsg} type='error' onDismiss={() => setToastMsg('')} />}
    </div>
  );
}
